import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useBlocker } from 'react-router-dom';
import grapesjs from 'grapesjs'
import 'grapesjs/dist/css/grapes.min.css'
import './GrapesJSSidebar.css'
import 'grapesjs/dist/grapes.min.js'
import webPlugin from 'grapesjs-preset-webpage'
import formPlugin from 'grapesjs-plugin-forms'
import basicPlugin from 'grapesjs-blocks-basic'
import ReactText from "./simple-react-text";
import BaseReactComponent from './base-react-component';
import ReactComponents from './react-components';
import MuiComponents from './epos';
import grapejsRulers from 'grapesjs-rulers';
import 'grapesjs-rulers/dist/grapesjs-rulers.min.css';
import tabs from 'grapesjs-tabs';
import flexbox from 'grapesjs-blocks-flexbox'
import AudienceSelector from './components/AudienceSelector';
import VersionHistory from './components/VersionHistory';
import BackgroundImagePickerModal from './components/BackgroundImagePickerModal';
import FontManagerModal from './components/FontManagerModal';
import { audiencesAPI } from './api/audiences';
import { websitesAPI } from './api/websites';
import { fontsAPI } from './api/fonts';
import { applyCustomFonts } from './grapesjs/customFonts';
import { injectGlobalCss } from './grapesjs/globalStyles';
import { registerBackgroundImageProperty } from './grapesjs/registerBackgroundImageProperty';
import { registerCustomCssProperty } from './grapesjs/registerCustomCssProperty';
import { registerDeviceVisibilityProperty } from './grapesjs/registerDeviceVisibilityProperty';
import { syncCustomCssRecursive } from './grapesjs/customCss';
import { setupMarqueeSelection } from './grapesjs/marqueeSelection';
import { setupGroupDrag } from './grapesjs/groupDrag';
import { setupLayerContextMenu } from './grapesjs/layerContextMenu';
import {
  setBackgroundImagePickerHandler,
  clearBackgroundImagePickerHandler,
  requestOpenBackgroundImagePicker,
} from './grapesjs/editorBridge';
import {
  normalizeBackgroundImageValue,
  extractUrlFromBackgroundImage,
} from './utils/backgroundImageCss';
import './WebBuilder.css';

const UNSAVED_CHANGES_LEAVE_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave?';

/** Shared responsive breakpoints (desktop ≥1025px, tablet 769–1024px, mobile ≤768px). */
const RESPONSIVE_BREAKPOINTS = {
  desktopMin: '1025px',
  tabletMin: '769px',
  tabletMax: '1024px',
  mobileMax: '768px',
};

const COLUMN_ROW_HEIGHT_PX = 75;

const ROW_GUTTER_PROP = '--gjs-row-gutter';
const ROW_MAX_DESKTOP_PROP = '--gjs-row-max-width-desktop';
const ROW_MAX_TABLET_PROP = '--gjs-row-max-width-tablet';
const ROW_MAX_MOBILE_PROP = '--gjs-row-max-width-mobile';
const ROW_LAYOUT_STYLE_KEYS = [
  ROW_GUTTER_PROP,
  ROW_MAX_DESKTOP_PROP,
  ROW_MAX_TABLET_PROP,
  ROW_MAX_MOBILE_PROP,
];

function stripManagedCssBlock(css, marker) {
  if (!css || !css.includes(marker)) return css || '';
  const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`/\\*\\s*${escaped}\\s*\\*/[\\s\\S]*?(?=\\/\\*\\s*gjs-|$)`, 'i');
  return css.replace(re, '').trim();
}

const MANAGED_CSS_MARKERS = [
  'gjs-device-visibility',
  'gjs-blocks-basic-columns',
  'gjs-row-layout',
];

/** True when saved CSS still embeds legacy blocks-basic column layout rules. */
function cssNeedsLegacyColumnMigration(css) {
  if (!css) return false;
  return (
    /\.gjs-row\s*\{[^}]*display\s*:\s*table/i.test(css) ||
    /\.gjs-cell\s*\{[^}]*display\s*:\s*table-cell/i.test(css) ||
    (/\[data-gjs-show-mobile="0"\]/i.test(css) &&
      /\.gjs-(?:cell|cell30|cell70)\s*\{/i.test(css))
  );
}

/**
 * GrapesJS expects component.getSelectors() to return a Selectors collection.
 * A full editor.setStyle() wipe can briefly leave classes as a raw string/array;
 * re-init before Style Manager reads selectors on selection.
 */
function ensureComponentSelectors(component) {
  if (!component || typeof component.get !== 'function') return;
  let selectors;
  try {
    selectors = typeof component.getSelectors === 'function' ? component.getSelectors() : null;
  } catch (_) {
    selectors = null;
  }
  if (!selectors || typeof selectors.getValid !== 'function') {
    if (typeof component.initClasses === 'function') {
      component.initClasses();
    }
  }
}

function ensureAllComponentSelectors(editor) {
  const wrapper = editor?.getWrapper?.();
  if (!wrapper || typeof wrapper.onAll !== 'function') return;
  wrapper.onAll((component) => ensureComponentSelectors(component));
}

function buildDeviceVisibilityCss() {
  const { desktopMin, tabletMin, tabletMax, mobileMax } = RESPONSIVE_BREAKPOINTS;
  // Primary selector is the trait-driven data-gjs-hide-* attribute. The legacy
  // data-gjs-show-*="0" selector is kept so pages saved before the trait
  // refactor keep rendering correctly.
  return `
/* gjs-device-visibility */
@media (min-width: ${desktopMin}) {
  [data-gjs-hide-desktop="1"], [data-gjs-show-desktop="0"] { display: none !important; }
}
@media (min-width: ${tabletMin}) and (max-width: ${tabletMax}) {
  [data-gjs-hide-tablet="1"], [data-gjs-show-tablet="0"] { display: none !important; }
}
@media (max-width: ${mobileMax}) {
  [data-gjs-hide-mobile="1"], [data-gjs-show-mobile="0"] { display: none !important; }
}`;
}

function buildColumnLayoutCss() {
  const { mobileMax } = RESPONSIVE_BREAKPOINTS;
  return `
/* gjs-blocks-basic-columns */
.gjs-row,
.row {
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  flex-wrap: nowrap;
}
@media (max-width: ${mobileMax}) {
  .gjs-row,
  .row {
    flex-wrap: wrap;
  }
  .gjs-cell,
  .gjs-cell30,
  .gjs-cell70,
  .cell {
    flex-basis: 100%;
    width: 100%;
  }
}
.gjs-cell,
.cell {
  min-height: ${COLUMN_ROW_HEIGHT_PX}px;
  flex-grow: 1;
  flex-basis: 100%;
}
.gjs-cell30 {
  flex-basis: 30%;
}
.gjs-cell70 {
  flex-basis: 70%;
}`;
}

function buildRowLayoutCss() {
  const { tabletMin, tabletMax, mobileMax } = RESPONSIVE_BREAKPOINTS;
  return `
/* gjs-row-layout */
.gjs-row,
.row {
  width: 100%;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  gap: var(${ROW_GUTTER_PROP}, 10px);
  padding: var(${ROW_GUTTER_PROP}, 10px);
  max-width: var(${ROW_MAX_DESKTOP_PROP}, none);
}
@media (min-width: ${tabletMin}) and (max-width: ${tabletMax}) {
  .gjs-row,
  .row {
    max-width: var(${ROW_MAX_TABLET_PROP}, var(${ROW_MAX_DESKTOP_PROP}, none));
  }
}
@media (max-width: ${mobileMax}) {
  .gjs-row,
  .row {
    max-width: var(${ROW_MAX_MOBILE_PROP}, var(${ROW_MAX_TABLET_PROP}, var(${ROW_MAX_DESKTOP_PROP}, none)));
  }
}`;
}

/** Remove legacy blocks-basic embedded rules so managed global CSS can replace them. */
function migrateLegacyColumnCss(css) {
  if (!css) return '';
  let next = css;
  next = next.replace(/\.gjs-row\s*\{[^}]*display\s*:\s*(?:table|flex)[^}]*\}/gi, '');
  next = next.replace(/\.gjs-cell30\s*\{[^}]*\}/gi, '');
  next = next.replace(/\.gjs-cell70\s*\{[^}]*\}/gi, '');
  next = next.replace(
    /\.gjs-cell\s*\{[^}]*(?:display\s*:\s*table-cell|flex-grow|width\s*:\s*100%)[^}]*\}/gi,
    ''
  );
  next = next.replace(
    /@media\s*\(\s*max-width\s*:\s*[\d.]+px\s*\)\s*\{\s*\.gjs-row\s*\{[^}]*flex-wrap[^}]*\}\s*\}/gi,
    ''
  );
  next = next.replace(
    /@media\s*\(\s*max-width\s*:\s*[\d.]+px\s*\)\s*\{\s*\.gjs-(?:cell|cell30|cell70)[^{}]*\{[^}]*\}\s*\}/gi,
    ''
  );
  next = next.replace(
    /@media\s*\(\s*max-width\s*:\s*[\d.]+px\s*\)\s*\{\s*\[data-gjs-show-mobile="0"\]\s*\{[^}]*\}(?:\s*\.gjs-(?:cell|cell30|cell70)\s*\{[^}]*\})?\s*\}/gi,
    ''
  );
  next = next.replace(
    /@media\s*\(\s*min-width\s*:\s*1025px\s*\)\s*\{\s*\[data-gjs-show-desktop="0"\]\s*\{[^}]*\}\s*\}/gi,
    ''
  );
  next = next.replace(
    /@media\s*\(\s*min-width\s*:\s*769px\s*\)\s*and\s*\(\s*max-width\s*:\s*1024px\s*\)\s*\{\s*\[data-gjs-show-tablet="0"\]\s*\{[^}]*\}\s*\}/gi,
    ''
  );
  next = next.replace(
    /@media\s*\(\s*max-width\s*:\s*768px\s*\)\s*\{\s*\[data-gjs-show-mobile="0"\]\s*\{[^}]*\}\s*\}/gi,
    ''
  );
  return next.replace(/\n{3,}/g, '\n\n').trim();
}

function normalizeRowLayoutStyleValue(propertyName, value) {
  const s = String(value ?? '').trim();
  if (s === '') return '';
  if (/^-?[\d.]+(px|%|rem|em|vw)$/i.test(s)) return s;
  if (/^-?[\d.]+$/.test(s)) return `${s}px`;
  return s;
}

/** Remove leading linear-gradient(...) layer we use as image overlay (must be first layer). */
function stripOverlayGradientFromBackgroundImage(bgImage) {
  const s = String(bgImage || '').trim();
  if (!s || s.toLowerCase() === 'none') return s;
  const m = /^\s*linear-gradient\s*\(/i.exec(s);
  if (!m) return s;
  let i = m.index + m[0].length;
  let depth = 1;
  for (; i < s.length && depth > 0; i++) {
    const c = s[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
  }
  const rest = s.slice(i).trim();
  if (rest.startsWith(',')) return rest.slice(1).trim();
  return s;
}

function parseColorToRgbaForOverlay(colorInput, alpha) {
  const a = Math.min(1, Math.max(0, Number(alpha) || 0));
  const c = String(colorInput || '').trim();
  if (!c) return `rgba(0,0,0,${a})`;
  if (/^#/i.test(c)) {
    let hex = c.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((ch) => ch + ch)
        .join('');
    }
    if (hex.length !== 6) return `rgba(0,0,0,${a})`;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  const rgbMatch = c.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]},${rgbMatch[2]},${rgbMatch[3]},${a})`;
  }
  return `rgba(0,0,0,${a})`;
}

function composeBackgroundImageWithOverlay(baseImage, color, opacity) {
  const base = String(baseImage || '').trim();
  if (!base || base.toLowerCase() === 'none') return base || '';
  const op = Number(opacity);
  if (!Number.isFinite(op) || op <= 0) return base;
  const rgba = parseColorToRgbaForOverlay(color, op);
  const grad = `linear-gradient(${rgba}, ${rgba})`;
  return `${grad}, ${base}`;
}

function parseCssPropFromStyleString(styleString, propName) {
  if (!styleString) return '';
  const escaped = propName.replace(/-/g, '\\-');
  const match = String(styleString).match(new RegExp(`${escaped}\\s*:\\s*([^;]+)`, 'i'));
  return match ? match[1].trim() : '';
}

function parseRotationDegFromTransform(transform) {
  const m = String(transform || '').match(/rotate\(\s*(-?[\d.]+)deg\s*\)/i);
  return m ? Number(m[1]) : 0;
}

/** background-image may live only on the attribute, in CssComposer, or on the canvas el on first load. */
function resolveComponentBackgroundImage(component) {
  if (!component) return '';

  const fromModel = () => {
    const style = component.getStyle?.() || {};
    const raw = style['background-image'];
    if (raw != null && String(raw).trim() !== '') return String(raw).trim();
    try {
      const inlineStyle = component.getStyle?.({ inline: true }) || {};
      const inlineRaw = inlineStyle['background-image'];
      if (inlineRaw != null && String(inlineRaw).trim() !== '') return String(inlineRaw).trim();
    } catch (_) {
      /* getStyle({ inline: true }) unsupported in some builds */
    }
    return '';
  };

  const fromAttrs = () => {
    const attrs = component.getAttributes?.() || {};
    const parsed = parseCssPropFromStyleString(attrs.style, 'background-image');
    if (!parsed || parsed.toLowerCase() === 'none') return '';
    const base = stripOverlayGradientFromBackgroundImage(parsed);
    const url = extractUrlFromBackgroundImage(base);
    if (!url) return String(parsed).trim();
    return normalizeBackgroundImageValue(url);
  };

  const fromCanvasEl = () => {
    const el = component.getView?.()?.el;
    const win = el?.ownerDocument?.defaultView;
    if (!el || !win) return '';
    const computed = win.getComputedStyle(el).backgroundImage;
    if (!computed || computed === 'none') return '';
    return String(computed).trim();
  };

  return fromModel() || fromAttrs() || fromCanvasEl();
}

/** background-color for tint overlay — model/attrs only (computed inherits parent). */
function resolveExplicitComponentBackgroundColor(component) {
  if (!component) return '';

  const style = component.getStyle?.() || {};
  const raw = style['background-color'];
  if (raw != null && String(raw).trim() !== '') return String(raw).trim();

  try {
    const inlineStyle = component.getStyle?.({ inline: true }) || {};
    const inlineRaw = inlineStyle['background-color'];
    if (inlineRaw != null && String(inlineRaw).trim() !== '') {
      return String(inlineRaw).trim();
    }
  } catch (_) {
    /* ignore */
  }

  return parseCssPropFromStyleString(
    component.getAttributes?.()?.style,
    'background-color',
  );
}

function isShapeComponentModel(component) {
  if (!component) return false;
  if (component.get?.('type') === 'shape') return true;
  const attrs = component.getAttributes?.() || {};
  if (attrs['data-gjs-shape']) return true;
  const classes = component.getClasses?.() || [];
  if (classes.some((c) => c === 'gjs-shape' || /^gjs-shape-/.test(c))) return true;
  return /\bgjs-shape\b/.test(attrs.class || '');
}

function reconcileBackgroundImageOverlay(component) {
  if (!component || typeof component.getStyle !== 'function') return;

  // Shapes store background/size in per-instance CSS rules, not getStyle().
  // resolveComponentBackgroundImage() falls back to computed canvas styles, so
  // reconcile would call addStyle() in a loop on every component:update.
  if (isShapeComponentModel(component)) return;

  const raw = resolveComponentBackgroundImage(component);
  if (!raw) return;

  const base = stripOverlayGradientFromBackgroundImage(raw);

  // Only manage the tint overlay when the background actually references an
  // image URL. Gradient-only (or other non-image) backgrounds must be left
  // untouched: normalizing them would wrap the value in url("…"), producing
  // invalid CSS and making the background disappear on selection.
  if (!extractUrlFromBackgroundImage(base)) return;

  const normalizedBase = normalizeBackgroundImageValue(base);
  if (!normalizedBase || normalizedBase.toLowerCase() === 'none') return;

  const bgColorRaw = resolveExplicitComponentBackgroundColor(component);
  const hasBgColor =
    bgColorRaw !== '' && !/^transparent$/i.test(bgColorRaw);

  const tintColor = hasBgColor ? bgColorRaw : undefined;
  const tintOpacity = hasBgColor ? 0.5 : 0;

  const composed = composeBackgroundImageWithOverlay(normalizedBase, tintColor, tintOpacity);
  const modelBgImageNow = String(component.getStyle()?.['background-image'] || '').trim();

  if (composed !== modelBgImageNow) {
    component.addStyle({ 'background-image': composed });
  }

  const blendNow = String(component.getStyle()?.['background-blend-mode'] || '').trim();
  if (hasBgColor) {
    if (blendNow !== 'overlay') {
      component.addStyle({ 'background-blend-mode': 'overlay' });
    }
  } else if (blendNow === 'overlay') {
    component.removeStyle('background-blend-mode');
  }
}

function reconcileBackgroundImageOverlayRecursive(component) {
  if (!component) return;
  reconcileBackgroundImageOverlay(component);
  const children = component.get('components');
  if (children && children.length > 0) {
    children.each((child) => reconcileBackgroundImageOverlayRecursive(child));
  }
}

// Helper function to extract all background properties from inline styles and add to component styles
const extractingBackgroundProps = new Set();
const extractBackgroundPropertiesFromStyles = (component) => {
  if (!component) return;
  const guardKey = component.cid || component;
  if (extractingBackgroundProps.has(guardKey)) return;
  extractingBackgroundProps.add(guardKey);
  try {
  // Shape components divert background/size props into per-instance CSS rules.
  // Skip extraction for shapes; still recurse into children.
  const isShape = isShapeComponentModel(component);
  const attributes = component.getAttributes();

  if (!isShape && attributes.style) {
    const styleString = attributes.style;
    const backgroundProps = {};
    let hasBackgroundProps = false;
    
    // Parse all background-related CSS properties from inline styles
    const backgroundProperties = [
      'background-image',
      'background-repeat',
      'background-position',
      'background-size',
      'background-attachment',
      'background-color',
      'background-clip',
      'background-origin',
      '--gjs-parallax',
      '--gjs-parallax-speed',
    ];
    
    backgroundProperties.forEach(prop => {
      // Match property: value (handles various formats)
      const regex = new RegExp(`${prop.replace('-', '\\-')}\\s*:\\s*([^;]+)`, 'i');
      const match = styleString.match(regex);
      if (match) {
        let value = match[1].trim();
        
        // Special handling for background-image - ensure url() format
        if (prop === 'background-image' && value.toLowerCase() === 'none') {
          return;
        }
        if (prop === 'background-image' && value) {
          const base = stripOverlayGradientFromBackgroundImage(value);
          const url = extractUrlFromBackgroundImage(base);
          if (url) {
            value = normalizeBackgroundImageValue(url);
          } else if (!/^url\s*\(/i.test(value)) {
            value = `url("${value}")`;
          }
        }
        
        backgroundProps[prop] = value;
        hasBackgroundProps = true;
      }
    });
    
    // If we found any background properties, add them to the component's style
    if (hasBackgroundProps) {
      const currentStyle = component.getStyle();
      let needsUpdate = false;
      
      // Check if any properties need to be updated
      Object.keys(backgroundProps).forEach(prop => {
        const currentValue = currentStyle[prop];
        const hasCurrentValue = currentValue !== undefined && String(currentValue).trim() !== '';
        if (!hasCurrentValue && currentStyle[prop] !== backgroundProps[prop]) {
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        component.addStyle(backgroundProps);
        // Force update the style manager to show the values
        component.trigger('change:style');
      }
    }
  }
  
  // Recursively process children
  const components = component.get('components');
  if (components && components.length > 0) {
    components.each((child) => {
      extractBackgroundPropertiesFromStyles(child);
    });
  }
  } finally {
    extractingBackgroundProps.delete(guardKey);
  }
};

function WebBuilder() {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [websiteName, setWebsiteName] = useState('');
  const [websiteDescription, setWebsiteDescription] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [isSavingWebsite, setIsSavingWebsite] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [blobUrls, setBlobUrls] = useState({});
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [storageTargets, setStorageTargets] = useState([]);
  const [websiteStorageTargetId, setWebsiteStorageTargetId] = useState('');
  const [audienceContentRevision, setAudienceContentRevision] = useState(0);
  const [bgImagePickerOpen, setBgImagePickerOpen] = useState(false);
  const [bgImagePickerCurrentUrl, setBgImagePickerCurrentUrl] = useState('');
  const [bgImagePickerTitle, setBgImagePickerTitle] = useState('Background image');
  const [fontManagerOpen, setFontManagerOpen] = useState(false);
  const [globalCss, setGlobalCss] = useState('');
  const [globalCssDraft, setGlobalCssDraft] = useState('');
  const [isSavingGlobalCss, setIsSavingGlobalCss] = useState(false);
  const bgImagePickerOnSelectRef = useRef(null);
  const customFontsRef = useRef([]);
  const globalCssRef = useRef('');
  const editorRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef({ html: '', css: '' });

  useEffect(() => {
    websitesAPI
      .getStorageTargets()
      .then(setStorageTargets)
      .catch(() => setStorageTargets([]));
  }, []);

  // Load custom (uploaded) fonts and register them with the editor's font picker + canvas.
  const loadCustomFonts = useCallback(async () => {
    if (!websiteId) return;
    try {
      const data = await fontsAPI.list(websiteId);
      const fonts = Array.isArray(data.fonts) ? data.fonts : [];
      customFontsRef.current = fonts;
      if (editorRef.current) {
        applyCustomFonts(editorRef.current, fonts);
      }
    } catch (err) {
      console.error('Failed to load custom fonts:', err);
    }
  }, [websiteId]);

  useEffect(() => {
    loadCustomFonts();
  }, [loadCustomFonts]);

  // Load website name and description
  useEffect(() => {
    if (websiteId) {
      websitesAPI.getById(websiteId)
        .then(website => {
          setWebsiteName(website.name || '');
          setWebsiteDescription(website.description || '');
          setEditedName(website.name || '');
          setEditedDescription(website.description || '');
          setWebsiteStorageTargetId(website.storageTargetId ? String(website.storageTargetId) : '');
          const css = website.globalCss || '';
          globalCssRef.current = css;
          setGlobalCss(css);
          setGlobalCssDraft(css);
          if (editorRef.current) {
            injectGlobalCss(editorRef.current, css);
          }
        })
        .catch(err => {
          console.error('Failed to load website:', err);
          navigate('/');
        });
    }
  }, [websiteId, navigate]);

  // Handle saving website name and description
  const handleSaveWebsiteInfo = async () => {
    if (!websiteId || isSavingWebsite) return;

    setIsSavingWebsite(true);
    try {
      await websitesAPI.update(websiteId, {
        name: editedName,
        description: editedDescription,
        storageTargetId: websiteStorageTargetId === '' ? null : websiteStorageTargetId,
      });
      setWebsiteName(editedName);
      setWebsiteDescription(editedDescription);
      setIsEditingName(false);
      setIsEditingDescription(false);
    } catch (err) {
      console.error('Failed to save website info:', err);
      alert(err.message || 'Failed to save website information');
      // Revert changes on error
      setEditedName(websiteName);
      setEditedDescription(websiteDescription);
    } finally {
      setIsSavingWebsite(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(websiteName);
    setEditedDescription(websiteDescription);
    setIsEditingName(false);
    setIsEditingDescription(false);
  };

  const handleStartEditName = () => {
    setEditedName(websiteName);
    setIsEditingName(true);
  };

  const handleStartEditDescription = () => {
    setEditedDescription(websiteDescription);
    setIsEditingDescription(true);
  };

  // Initialize editor once per website
  useEffect(() => {
    if (!websiteId) return;

    // Clear the container before initializing
    const container = document.getElementById('gjs');
    if (container) {
      container.innerHTML = '';
    }

    const googleFontsApiKey = process.env.REACT_APP_GOOGLE_FONTS_API_KEY || '';

    // GrapesJS 0.20: StyleManager has no getBuiltIn; @silexlabs/grapesjs-fonts expects it.
    const ensureStyleManagerGetBuiltInFontFamily = (editor) => {
      const sm = editor?.StyleManager;
      if (!sm || typeof sm.getBuiltIn === 'function') return;
      sm.getBuiltIn = function getBuiltIn(name) {
        if (name !== 'font-family') return { options: [] };
        let options = [];
        if (typeof sm.getSectors === 'function') {
          const sectors = sm.getSectors();
          const sectorList = Array.isArray(sectors) ? sectors : sectors?.models || [];
          for (const sector of sectorList) {
            const props = typeof sector.getProperties === 'function' ? sector.getProperties() : [];
            const propList = Array.isArray(props) ? props : props?.models || [];
            const found = propList.find((p) => {
              const propName = p.getName ? p.getName() : p.get?.('name');
              const cssProp = p.get ? p.get('property') : null;
              return propName === 'font-family' || cssProp === 'font-family';
            });
            if (found && typeof found.get === 'function') {
              const opts = found.get('options');
              if (Array.isArray(opts)) {
                options = opts;
                break;
              }
            }
          }
        }
        return { options };
      };
    };

    if (googleFontsApiKey) {
      const fontsGlobal = typeof window !== 'undefined' ? window['@silexlabs/grapesjs-fonts'] : null;
      const fontsFactory = fontsGlobal?.default || fontsGlobal;
      if (typeof fontsFactory !== 'function') {
        console.warn(
          '@silexlabs/grapesjs-fonts global not found. Ensure public/index.html includes the unpkg script for the fonts plugin.'
        );
      } else if (!grapesjs.plugins.get('@silexlabs/grapesjs-fonts')) {
        grapesjs.plugins.add('@silexlabs/grapesjs-fonts', (editor, opts) => {
          ensureStyleManagerGetBuiltInFontFamily(editor);
          fontsFactory(editor, opts);
        });
      }
    }

    const editor = grapesjs.init({
      container: '#gjs',
      height: '1024px',
      width: '100%',
      allowScripts: true,
      multipleSelection: true,
      selectorManager: {
        componentFirst: true, // Optimizes target management for multiple selections
      },
      plugins: [
        basicPlugin,
        formPlugin,
        webPlugin,
        BaseReactComponent,
        ReactComponents,
        MuiComponents,
        grapejsRulers,
        tabs,
        flexbox,
        ...(googleFontsApiKey ? ['@silexlabs/grapesjs-fonts'] : []),
      ],
      storageManager: {
        id: `gjs-${websiteId}-`,
        type: 'local',
        autosave: false, // We'll handle saving manually to the server
        storeComponents: false,
        storeStyles: false,
        storeHtml: false,
        storeCss: false,
      },
      deviceManager: {
        devices: [
          {
            id: 'desktop',
            name: 'Desktop',
            width: '',
          },
          {
            id: 'tablet',
            name: 'Tablet',
            width: RESPONSIVE_BREAKPOINTS.tabletMax,
            height: '1024px',
            widthMedia: RESPONSIVE_BREAKPOINTS.tabletMax,
          },
          {
            id: 'mobilePortrait',
            name: 'Mobile portrait',
            width: '393px',
            height: '852px',
            widthMedia: RESPONSIVE_BREAKPOINTS.mobileMax,
          },
        ]
      },
      pluginsOpts: {
        'gjs-blocks-basic': {
          flexGrid: true,
          addBasicStyle: false,
        },
        'grapesjs-preset-webpage': {
          blocks: ['column1', 'column2', 'column3', 'column3-7', 'text', 'link', 'image', 'video', 'map'],
        },
        'grapesjs-rulers': {
          // Preserve project's existing absolute drag mode and full zoom when rulers are toggled.
          dragMode: 'absolute',
          canvasZoom: 100,
          rulerHeight: 15,
        },
        ...(googleFontsApiKey
          ? {
              '@silexlabs/grapesjs-fonts': {
                api_key: googleFontsApiKey,
              },
            }
          : {}),
      },
    });

    editor.BlockManager.add('my-block-id', {
      label: 'ReactText',
      category: 'ePOS',
      components: ReactText,
      activate: true
    });

    // Add a toggle button for grapesjs-rulers in the default options panel.
    const rulersOptionsPanel = editor.Panels.getPanel('options');
    if (rulersOptionsPanel && !rulersOptionsPanel.get('buttons').get('ruler-visibility')) {
      rulersOptionsPanel.get('buttons').add([
        {
          id: 'ruler-visibility',
          command: 'ruler-visibility',
          // `context` prevents the command from being auto-stopped when other
          // options-panel buttons (preview, fullscreen, etc.) are clicked.
          context: 'ruler-visibility',
          attributes: { title: 'Toggle Rulers & Guides' },
          label:
            '<svg width="18" viewBox="0 0 16 16"><path fill="currentColor" d="M0 8a.5.5 0 0 1 .5-.5h15a.5.5 0 0 1 0 1H.5A.5.5 0 0 1 0 8z"/><path fill="currentColor" d="M4 3h8a1 1 0 0 1 1 1v2.5h1V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2.5h1V4a1 1 0 0 1 1-1zM3 9.5H2V12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V9.5h-1V12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/></svg>',
        },
      ]);
    }

    // Disable rulers automatically when entering preview/fullscreen modes.
    editor.on('run:preview', () => editor.stopCommand('ruler-visibility'));
    editor.on('run:fullscreen', () => editor.stopCommand('ruler-visibility'));

    // Turn rulers (and guides) on by default once the canvas is ready.
    editor.on('load', () => {
      editor.runCommand('ruler-visibility');
      editor.runCommand('guides-visibility');
      // Re-apply uploaded fonts now that the canvas document + Style Manager exist.
      if (customFontsRef.current && customFontsRef.current.length) {
        applyCustomFonts(editor, customFontsRef.current);
      }
      // Inject website-level global styles into the canvas for live preview.
      injectGlobalCss(editor, globalCssRef.current);
    });

    const setTranslateDragModeForType = (typeName) => {
      const existingType = editor.DomComponents.getType(typeName);
      const baseDefaults = existingType?.model?.prototype?.defaults || {};

      editor.DomComponents.addType(typeName, {
        extend: existingType ? typeName : 'div',
        model: {
          defaults: {
            ...baseDefaults,
            dragMode: 'translate',
          },
        },
      });
    };

    // Column presets from grapesjs-preset-webpage are built on row/cell-style types.
    // Force these types to use translate drag behavior.
    ['row', 'cell', 'column'].forEach((typeName) => {
      setTranslateDragModeForType(typeName);
    });

    const SHAPE_RESIZABLE = {
      tl: 1,
      tc: 1,
      tr: 1,
      cl: 1,
      cr: 1,
      bl: 1,
      bc: 1,
      br: 1,
      minDim: 24,
      keyWidth: 'width',
      keyHeight: 'height',
    };

    const STAR_CLIP =
      'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)';

    const SHAPE_SIZE_DEFAULTS = {
      rectangle: { width: 160, height: 100 },
      square: { width: 100, height: 100 },
      circle: { width: 100, height: 100 },
      star: { width: 100, height: 100 },
    };

    const SHAPE_VARIANT_DEFAULTS = {
      rectangle: {
        'border-radius': '4px',
        'background-color': '#6366f1',
      },
      square: {
        'border-radius': '4px',
        'background-color': '#8b5cf6',
      },
      circle: {
        'border-radius': '50%',
        'background-color': '#ec4899',
      },
      star: {
        'border-radius': '0',
        'background-color': '#f59e0b',
        'clip-path': STAR_CLIP,
        '-webkit-clip-path': STAR_CLIP,
      },
    };

    const SHAPE_DEFAULTS_STYLE_ID = 'gjs-shape-defaults';
    const SHAPE_DEFAULT_CLASS = 'gjs-shape-default';
    const SHAPE_CUSTOM_CLASS = 'gjs-shape-custom';

    const SHAPE_INSTANCE_STYLE_PROPS = new Set([
      'width',
      'height',
      'min-width',
      'min-height',
      'max-width',
      'max-height',
      'background-color',
      'border-radius',
      'clip-path',
      '-webkit-clip-path',
      'opacity',
      'border',
      'border-width',
      'border-color',
      'border-style',
    ]);

    /** Base defaults on variant classes; instance + custom classes override with higher specificity. */
    const buildShapeDefaultsCss = () => {
      let css = `/* gjs-shape-defaults */
.gjs-shape { display: block; box-sizing: border-box; }
`;
      Object.keys(SHAPE_SIZE_DEFAULTS).forEach((variant) => {
        const { width, height } = SHAPE_SIZE_DEFAULTS[variant];
        const visual = SHAPE_VARIANT_DEFAULTS[variant] || {};
        css += `.gjs-shape.${SHAPE_DEFAULT_CLASS}.gjs-shape-${variant} {\n`;
        css += `  width: ${width}px;\n  height: ${height}px;\n`;
        Object.entries(visual).forEach(([prop, value]) => {
          css += `  ${prop}: ${value};\n`;
        });
        css += '}\n';
      });
      return css;
    };

    const injectShapeDefaultsIntoCanvas = () => {
      const doc = editor.Canvas.getDocument?.();
      if (!doc) return;
      let el = doc.getElementById(SHAPE_DEFAULTS_STYLE_ID);
      if (!el) {
        el = doc.createElement('style');
        el.id = SHAPE_DEFAULTS_STYLE_ID;
        (doc.head || doc.body).appendChild(el);
      }
      el.textContent = buildShapeDefaultsCss();
    };

    const getShapeVariant = (component) => {
      const attrs = component.getAttributes?.() || {};
      if (attrs['data-gjs-shape']) return attrs['data-gjs-shape'];
      const classes = component.getClasses?.() || [];
      const variantClass = classes.find((c) =>
        /^gjs-shape-(rectangle|square|circle|star)$/.test(c)
      );
      if (variantClass) return variantClass.replace('gjs-shape-', '');
      return 'rectangle';
    };

    const isShapeComponent = isShapeComponentModel;

    const ensureShapeVariantClass = (component) => {
      if (!isShapeComponent(component)) return;
      const variant = getShapeVariant(component);
      const variantClass = `gjs-shape-${variant}`;
      if (!component.getClasses().includes('gjs-shape')) {
        component.addClass('gjs-shape');
      }
      if (!component.getClasses().includes(SHAPE_DEFAULT_CLASS)) {
        component.addClass(SHAPE_DEFAULT_CLASS);
      }
      if (!component.getClasses().includes(variantClass)) {
        component.addClass(variantClass);
      }
    };

    const getShapeInstanceSelector = (component) => {
      const variant = getShapeVariant(component);
      const cid = component.cid || component.getId?.();
      if (!cid) return null;
      return `.gjs-shape.${SHAPE_DEFAULT_CLASS}.gjs-shape-${variant}.${SHAPE_CUSTOM_CLASS}.gjs-shape-inst-${cid}`;
    };

    const splitShapeStyleInput = (prop, value) => {
      const instance = {};
      const other = {};
      const assign = (key, val) => {
        if (val == null || (typeof val === 'string' && val.trim() === '')) return;
        if (key === 'transform') {
          other[key] = val;
        } else if (SHAPE_INSTANCE_STYLE_PROPS.has(key)) {
          instance[key] = val;
        } else {
          other[key] = val;
        }
      };
      if (prop != null && typeof prop === 'object' && !Array.isArray(prop)) {
        Object.entries(prop).forEach(([key, val]) => assign(key, val));
      } else if (typeof prop === 'string') {
        assign(prop, value);
      }
      return { instance, other };
    };

    const getRawShapeComponentStyle = (component, opts = {}) => {
      if (!component) return {};
      let style = {};
      if (typeof component._shapeRawGetStyle === 'function') {
        style = { ...(component._shapeRawGetStyle(opts) || {}) };
      } else {
        const SuperCtor = component.constructor?.__super__;
        const proto =
          SuperCtor?.prototype ||
          editor.DomComponents.getType('div')?.model?.prototype ||
          editor.DomComponents.getType('default')?.model?.prototype;
        if (typeof proto?.getStyle === 'function') {
          try {
            style = { ...(proto.getStyle.call(component, opts) || {}) };
          } catch {
            style = { ...(component.get('style') || {}) };
          }
        } else {
          style = { ...(component.get('style') || {}) };
        }
      }
      // Legacy shapes often keep width/clip-path/background on the style attribute.
      const attrs = component.getAttributes?.() || {};
      if (attrs.style) {
        SHAPE_INSTANCE_STYLE_PROPS.forEach((key) => {
          const parsed = parseCssPropFromStyleString(attrs.style, key);
          if (parsed && (!style[key] || String(style[key]).trim() === '')) {
            style[key] = parsed;
          }
        });
      }
      return style;
    };

    const getShapeInstanceRuleStyle = (component) => {
      const selector = getShapeInstanceSelector(component);
      if (!selector) return {};
      const cssApi = editor.Css;
      const rule =
        cssApi && typeof cssApi.getRule === 'function' ? cssApi.getRule(selector) : null;
      return rule?.getStyle?.() ? { ...rule.getStyle() } : {};
    };

    const shapeStylesEqual = (a, b) => {
      const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
      for (const key of keys) {
        if (String(a?.[key] ?? '').trim() !== String(b?.[key] ?? '').trim()) {
          return false;
        }
      }
      return true;
    };

    const stripPropsFromAttrsStyleString = (component, propNames) => {
      const attrs = component.getAttributes?.() || {};
      let styleStr = attrs.style;
      if (!styleStr || typeof styleStr !== 'string') return;
      let updated = styleStr;
      propNames.forEach((prop) => {
        const escaped = prop.replace(/-/g, '\\-');
        updated = updated.replace(
          new RegExp(`(?:^|;)\\s*${escaped}\\s*:\\s*[^;]*`, 'gi'),
          ''
        );
      });
      updated = updated
        .replace(/^\s*;\s*|\s*;\s*$/g, '')
        .replace(/;\s*;/g, ';')
        .trim();
      if (updated !== styleStr.trim()) {
        component.addAttributes({ style: updated || undefined });
      }
    };

    const stripShapeComponentStyleProps = (component, propNames) => {
      if (!component || typeof component.removeStyle !== 'function') return;
      const raw = getRawShapeComponentStyle(component);
      const toStrip = propNames.filter((prop) => {
        const val = raw[prop];
        return val != null && String(val).trim() !== '';
      });
      if (toStrip.length === 0) return;
      toStrip.forEach((prop) => {
        try {
          component.removeStyle(prop);
        } catch (_) {
          /* ignore */
        }
        try {
          component.removeStyle(prop, { inline: true });
        } catch (_) {
          /* ignore */
        }
      });
      stripPropsFromAttrsStyleString(component, toStrip);
    };

    const setShapeInstanceRule = (component, styleObj) => {
      const selector = getShapeInstanceSelector(component);
      if (!selector || !styleObj) return;
      const cssApi = editor.Css;
      if (!cssApi || typeof cssApi.setRule !== 'function') return;

      const filtered = {};
      Object.entries(styleObj).forEach(([key, val]) => {
        if (val != null && String(val).trim() !== '') {
          filtered[key] = val;
        }
      });
      if (Object.keys(filtered).length === 0) return;

      const existingRule =
        typeof cssApi.getRule === 'function' ? cssApi.getRule(selector) : null;
      const merged = existingRule?.getStyle
        ? { ...existingRule.getStyle(), ...filtered }
        : filtered;

      if (!existingRule || !shapeStylesEqual(existingRule.getStyle?.() || {}, merged)) {
        cssApi.setRule(selector, merged);
      }
      if (!component.getClasses().includes(SHAPE_CUSTOM_CLASS)) {
        component.addClass(SHAPE_CUSTOM_CLASS);
      }
    };

    const applyShapeInstanceStyle = (component, styleObj) => {
      if (!styleObj || Object.keys(styleObj).length === 0) return;
      setShapeInstanceRule(component, styleObj);
      stripShapeComponentStyleProps(component, Object.keys(styleObj));
    };

    const migrateShapeComponentStylesToInstanceRule = (component) => {
      if (!isShapeComponent(component)) return;
      const style = getRawShapeComponentStyle(component);
      const toMove = {};
      SHAPE_INSTANCE_STYLE_PROPS.forEach((key) => {
        const val = style[key];
        if (val != null && String(val).trim() !== '') {
          toMove[key] = val;
        }
      });
      if (Object.keys(toMove).length > 0) {
        applyShapeInstanceStyle(component, toMove);
      }
    };

    const ensureShapeInstanceIdentity = (component) => {
      if (!isShapeComponent(component)) return;
      const cid = component.cid || component.getId?.();
      if (!cid) return;
      const instClass = `gjs-shape-inst-${cid}`;
      if (!component.getClasses().includes(instClass)) {
        component.addClass(instClass);
      }
      const attrs = component.getAttributes?.() || {};
      if (!attrs['data-shape-instance']) {
        component.addAttributes({ 'data-shape-instance': String(cid) });
      }
    };

    const finalizingShapes = new Set();
    const finalizeShapeComponent = (component) => {
      if (!isShapeComponent(component)) return;
      const key = component.cid || component;
      if (finalizingShapes.has(key)) return;
      finalizingShapes.add(key);
      try {
        ensureShapeVariantClass(component);
        ensureShapeInstanceIdentity(component);
        if (component.get('type') !== 'shape') {
          const attrs = component.getAttributes?.() || {};
          if (!attrs['data-gjs-shape']) {
            component.addAttributes({ 'data-gjs-shape': getShapeVariant(component) });
          }
          component.set('type', 'shape');
        }
        migrateShapeComponentStylesToInstanceRule(component);
      } finally {
        finalizingShapes.delete(key);
      }
    };

    const finalizeAllShapesRecursively = (component) => {
      if (!component) return;
      if (isShapeComponent(component)) finalizeShapeComponent(component);
      const children = component.get('components');
      if (children && children.length > 0) {
        children.each((child) => finalizeAllShapesRecursively(child));
      }
    };
    editor.__finalizeAllShapes = () => {
      editor.getComponents().each((c) => finalizeAllShapesRecursively(c));
    };

    editor.addStyle(buildShapeDefaultsCss());
    injectShapeDefaultsIntoCanvas();

    editor.DomComponents.addType('shape', {
      extend: 'default',
      model: {
        defaults: {
          type: 'shape',
          tagName: 'div',
          classes: ['gjs-shape', 'gjs-shape-default', SHAPE_DEFAULT_CLASS],
          attributes: {},
          style: {},
          draggable: true,
          droppable: false,
          hoverable: true,
          selectable: true,
          resizable: SHAPE_RESIZABLE,
          traits: [
            {
              type: 'number',
              name: 'rotationDeg',
              label: 'Rotation (°)',
              changeProp: 1,
              min: -180,
              max: 180,
              step: 1,
            },
          ],
          rotationDeg: 0,
        },
        init() {
          finalizeShapeComponent(this);
          const rawTransform = this._shapeRawGetStyle?.()?.transform;
          const parsedDeg = parseRotationDegFromTransform(rawTransform);
          const currentDeg = Number(this.get('rotationDeg'));
          if (
            parsedDeg !== 0 &&
            (!Number.isFinite(currentDeg) || currentDeg === 0)
          ) {
            this.set('rotationDeg', parsedDeg, { silent: true });
          }
          this.on('change:rotationDeg', this.syncShapeRotation);
          this.syncShapeRotation();
        },
        _shapeProtoMethod(methodName) {
          const SuperCtor = this.constructor.__super__;
          let proto = SuperCtor?.prototype;
          if (!proto?.[methodName]) {
            proto =
              editor.DomComponents.getType('div')?.model?.prototype ||
              editor.DomComponents.getType('default')?.model?.prototype;
          }
          return proto?.[methodName];
        },
        _shapeRawGetStyle(opts = {}) {
          const fn = this._shapeProtoMethod('getStyle');
          return fn ? fn.call(this, opts) || {} : this.get('style') || {};
        },
        getStyle(opts = {}) {
          const componentStyle = this._shapeRawGetStyle(opts);
          if (opts?.inline) return componentStyle;
          const ruleStyle = getShapeInstanceRuleStyle(this);
          return { ...ruleStyle, ...componentStyle };
        },
        setStyle(prop, opts = {}) {
          const value =
            opts && typeof opts === 'object' && opts !== null && 'value' in opts
              ? opts.value
              : opts;
          const { instance, other } = splitShapeStyleInput(prop, value);
          if (Object.keys(instance).length > 0) {
            applyShapeInstanceStyle(this, instance);
          }
          const otherKeys =
            typeof other === 'object' && other !== null ? Object.keys(other) : [];
          if (otherKeys.length === 0) return this;
          const setStyleFn = this._shapeProtoMethod('setStyle');
          if (!setStyleFn) return undefined;
          const baseOpts = opts && typeof opts === 'object' ? opts : {};
          const useInline = this._shapeStyleUsesInline(other);
          return setStyleFn.call(this, other, { ...baseOpts, inline: useInline });
        },
        addStyle(prop, opts = {}) {
          const value =
            opts && typeof opts === 'object' && opts !== null && 'value' in opts
              ? opts.value
              : opts;
          const { instance, other } = splitShapeStyleInput(prop, value);
          if (Object.keys(instance).length > 0) {
            applyShapeInstanceStyle(this, instance);
          }
          const otherKeys =
            typeof other === 'object' && other !== null ? Object.keys(other) : [];
          if (otherKeys.length === 0) return this;
          const addStyleFn = this._shapeProtoMethod('addStyle');
          if (!addStyleFn) return undefined;
          const baseOpts = opts && typeof opts === 'object' ? opts : {};
          const useInline = this._shapeStyleUsesInline(other);
          return addStyleFn.call(this, other, { ...baseOpts, inline: useInline });
        },
        _shapeStyleUsesInline(prop) {
          if (prop == null) return false;
          if (typeof prop === 'string') {
            return prop === 'transform';
          }
          if (typeof prop === 'object' && !Array.isArray(prop)) {
            const keys = Object.keys(prop);
            return keys.length > 0 && keys.every((k) => k === 'transform');
          }
          return false;
        },
        syncShapeRotation() {
          const raw = Number(this.get('rotationDeg'));
          const deg = Number.isFinite(raw) ? Math.max(-180, Math.min(180, raw)) : 0;
          if (deg !== raw) {
            this.set('rotationDeg', deg);
          }
          const nextTransform = deg === 0 ? 'none' : `rotate(${deg}deg)`;
          const current = String(this._shapeRawGetStyle()?.transform || '').trim();
          if (current === nextTransform) return;
          this.addStyle({ transform: nextTransform });
        },
      },
      isComponent: (el) => el?.classList?.contains('gjs-shape'),
    });

    const shapeBlockDefs = [
      {
        id: 'shape-rectangle',
        label: 'Rectangle',
        dataShape: 'rectangle',
        extraClass: 'gjs-shape-rectangle',
        media:
          '<svg viewBox="0 0 24 24" width="28" height="28"><rect x="3" y="7" width="18" height="12" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
      },
      {
        id: 'shape-square',
        label: 'Square',
        dataShape: 'square',
        extraClass: 'gjs-shape-square',
        media:
          '<svg viewBox="0 0 24 24" width="28" height="28"><rect x="5" y="5" width="14" height="14" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
      },
      {
        id: 'shape-circle',
        label: 'Circle',
        dataShape: 'circle',
        extraClass: 'gjs-shape-circle',
        media:
          '<svg viewBox="0 0 24 24" width="28" height="28"><circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
      },
      {
        id: 'shape-star',
        label: 'Star',
        dataShape: 'star',
        extraClass: 'gjs-shape-star',
        media:
          '<svg viewBox="0 0 24 24" width="28" height="28"><path d="M12 2.5l2.8 6.9 7.5.6-5.7 4.8 1.8 7.3L12 17.9 6.6 21.1l1.8-7.3L2.7 9 10.2 8.4z" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>',
      },
    ];

    shapeBlockDefs.forEach((def) => {
      editor.BlockManager.add(def.id, {
        label: def.label,
        category: 'Shapes',
        media: def.media,
        content: {
          type: 'shape',
          classes: ['gjs-shape', SHAPE_DEFAULT_CLASS, def.extraClass],
          attributes: {
            'data-gjs-shape': def.dataShape,
          },
          rotationDeg: 0,
        },
      });
    });

    const handleShapeComponentAdd = (component) => {
      if (!component) return;
      if (isShapeComponent(component)) {
        finalizeShapeComponent(component);
      }
      const children = component.get('components');
      if (children && children.length > 0) {
        children.each((child) => handleShapeComponentAdd(child));
      }
    };
    editor.on('component:add', handleShapeComponentAdd);

    // Static header: full-width block with adjustable height (trait syncs to style)
    editor.DomComponents.addType('static-header', {
      extend: 'div',
      model: {
        defaults: {
          tagName: 'header',
          attributes: { class: 'gjs-static-header' },
          style: {
            width: '100%',
            'min-width': '100%',
            height: '75px',
            'min-height': '75px',
            'max-height': '75px',
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            'z-index': '100',
            overflow: 'hidden',
            display: 'block',
            'box-sizing': 'border-box',
            'background-color': 'rgba(0,0,0,0.05)',
          },
          traits: [
            {
              type: 'number',
              name: 'headerHeight',
              label: 'Height (px)',
              min: 32,
              max: 500,
              changeProp: 1,
            },
          ],
          content: '',
          droppable: true,
          stylable: true,
          headerHeight: 75,
        },
        init() {
          this.on('change:headerHeight', this._syncHeightToStyle);
          this._syncHeightToStyle();
        },
        _syncHeightToStyle() {
          const px = Math.min(500, Math.max(32, Number(this.get('headerHeight')) || 75));
          this.addStyle({
            height: `${px}px`,
            'min-height': `${px}px`,
            'max-height': `${px}px`,
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            'z-index': '100',
            overflow: 'hidden',
          });
        },
      },
      isComponent: (el) => el && el.classList && el.classList.contains('gjs-static-header'),
    });

    editor.BlockManager.add('static-header-block', {
      label: 'Static Header',
      category: 'Basic',
      content: {
        type: 'static-header',
        style: {
          width: '100%',
          'min-width': '100%',
          height: '75px',
          'min-height': '75px',
          'max-height': '75px',
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          'z-index': '100',
          overflow: 'hidden',
          display: 'block',
          'box-sizing': 'border-box',
          'background-color': 'rgba(0,0,0,0.05)',
        },
        attributes: { class: 'gjs-static-header' },
        headerHeight: 75,
      },
      media: '<span class="gjs-fonts gjs-f-header">H</span>',
    });

    // Raw HTML block: allows multiline HTML snippets (eg. iframe embed code)
    editor.DomComponents.addType('raw-html', {
      extend: 'default',
      model: {
        defaults: {
          tagName: 'div',
          attributes: { class: 'gjs-raw-html-block' },
          stylable: true,
          droppable: false,
          editable: false,
          rawHtml: '',
          style: {},
          traits: [],
        },
        init() {
          this.removeLegacyEditorOnlyStyles();
          this.on('change:rawHtml', this.updateRawHtmlContent);
          this.updateRawHtmlContent();
        },
        removeLegacyEditorOnlyStyles() {
          const style = { ...(this.getStyle() || {}) };
          const legacyEditorStyles = {
            'min-height': '120px',
            padding: '16px',
            border: '2px dashed #94a3b8',
            'border-radius': '8px',
            'box-sizing': 'border-box',
            'background-color': 'rgba(148, 163, 184, 0.08)',
          };
          let changed = false;
          Object.entries(legacyEditorStyles).forEach(([key, value]) => {
            if (style[key] === value) {
              delete style[key];
              changed = true;
            }
          });
          if (changed) {
            this.setStyle(style);
          }
        },
        updateRawHtmlContent() {
          const rawHtml = this.get('rawHtml') || '';
          this.components(rawHtml);
        },
      },
      view: {
        events: {
          dblclick: 'openRawHtmlEditor',
          mouseenter: 'showEditorOverlay',
          mouseleave: 'hideEditorOverlay',
        },
        init() {
          this.listenTo(this.model, 'change:rawHtml', this.updatePlaceholderState);
          this.listenTo(this.model, 'change:components', this.updatePlaceholderState);
        },
        onRender() {
          this.applyEditorChrome();
          this.updatePlaceholderState();
        },
        applyEditorChrome() {
          this.el.style.minHeight = '120px';
          this.el.style.padding = '16px';
          this.el.style.border = '2px dashed #94a3b8';
          this.el.style.borderRadius = '8px';
          this.el.style.boxSizing = 'border-box';
          this.el.style.backgroundColor = 'rgba(148, 163, 184, 0.08)';
        },
        openRawHtmlEditor(event) {
          event.preventDefault();
          event.stopPropagation();
          const current = this.model.get('rawHtml') || '';

          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.flexDirection = 'column';
          container.style.gap = '12px';
          container.style.minWidth = '640px';
          container.style.maxWidth = '90vw';

          const textarea = document.createElement('textarea');
          textarea.value = current;
          textarea.placeholder = '<iframe src="https://..."></iframe>';
          textarea.style.width = '100%';
          textarea.style.minHeight = '320px';
          textarea.style.padding = '10px';
          textarea.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
          textarea.style.fontSize = '13px';
          textarea.style.lineHeight = '1.5';
          textarea.style.border = '1px solid #cbd5e1';
          textarea.style.borderRadius = '8px';
          textarea.style.boxSizing = 'border-box';
          textarea.style.resize = 'vertical';

          const actions = document.createElement('div');
          actions.style.display = 'flex';
          actions.style.justifyContent = 'flex-end';
          actions.style.gap = '8px';

          const cancelBtn = document.createElement('button');
          cancelBtn.type = 'button';
          cancelBtn.textContent = 'Cancel';
          cancelBtn.style.padding = '8px 12px';
          cancelBtn.style.border = '1px solid #cbd5e1';
          cancelBtn.style.borderRadius = '6px';
          cancelBtn.style.background = '#fff';
          cancelBtn.style.cursor = 'pointer';

          const saveBtn = document.createElement('button');
          saveBtn.type = 'button';
          saveBtn.textContent = 'Save';
          saveBtn.style.padding = '8px 12px';
          saveBtn.style.border = '1px solid #3b82f6';
          saveBtn.style.borderRadius = '6px';
          saveBtn.style.background = '#3b82f6';
          saveBtn.style.color = '#fff';
          saveBtn.style.cursor = 'pointer';

          cancelBtn.addEventListener('click', () => {
            editor.Modal.close();
          });

          const saveRawHtml = () => {
            const nextHtml = textarea.value || '';
            const scriptSrcMatches = Array.from(
              nextHtml.matchAll(/<script\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi)
            ).map((match) => match[1].trim());
            const externalScriptSrcs = scriptSrcMatches.filter((src) => /^(https?:)?\/\//i.test(src));

            if (externalScriptSrcs.length > 0) {
              const uniqueExternalSrcs = [...new Set(externalScriptSrcs)];
              const confirmed = window.confirm(
                `This HTML includes external script tag(s):\n\n${uniqueExternalSrcs.join('\n')}\n\nDo you want to allow these external scripts?`
              );
              if (!confirmed) {
                return;
              }
            }

            this.model.set('rawHtml', nextHtml);
            editor.Modal.close();
          };

          saveBtn.addEventListener('click', saveRawHtml);

          textarea.addEventListener('keydown', (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              saveRawHtml();
            }
          });

          actions.appendChild(cancelBtn);
          actions.appendChild(saveBtn);
          container.appendChild(textarea);
          container.appendChild(actions);

          editor.Modal.setTitle('Edit Raw HTML');
          editor.Modal.setContent(container);
          editor.Modal.open();
          setTimeout(() => textarea.focus(), 0);
        },
        updatePlaceholderState() {
          const rawHtml = (this.model.get('rawHtml') || '').trim();
          const existing = this.el.querySelector('[data-raw-html-placeholder="true"]');
          const existingOverlay = this.el.querySelector('[data-raw-html-editor-overlay="true"]');

          if (rawHtml) {
            if (existing) existing.remove();
            if (!existingOverlay) {
              this.createEditorOverlay();
            }
            return;
          }

          if (!existing) {
            const placeholder = document.createElement('div');
            placeholder.setAttribute('data-raw-html-placeholder', 'true');
            placeholder.textContent = 'Raw HTML';
            placeholder.style.width = '100%';
            placeholder.style.height = '100%';
            placeholder.style.minHeight = '84px';
            placeholder.style.display = 'grid';
            placeholder.style.placeItems = 'center';
            placeholder.style.fontSize = '14px';
            placeholder.style.fontWeight = '600';
            placeholder.style.color = '#475569';
            placeholder.style.letterSpacing = '0.02em';
            placeholder.style.pointerEvents = 'none';
            this.el.appendChild(placeholder);
          }

          if (!existingOverlay) {
            this.createEditorOverlay();
          }
        },
        createEditorOverlay() {
          const overlayBtn = document.createElement('button');
          overlayBtn.setAttribute('type', 'button');
          overlayBtn.setAttribute('data-raw-html-editor-overlay', 'true');
          overlayBtn.textContent = 'Edit Raw HTML';
          //overlayBtn.style.position = 'absolute';
          overlayBtn.style.top = '8px';
          overlayBtn.style.right = '8px';
          overlayBtn.style.zIndex = '3';
          overlayBtn.style.padding = '6px 10px';
          overlayBtn.style.border = '1px solid #3b82f6';
          overlayBtn.style.borderRadius = '6px';
          overlayBtn.style.background = '#3b82f6';
          overlayBtn.style.color = '#fff';
          overlayBtn.style.fontSize = '12px';
          overlayBtn.style.fontWeight = '600';
          overlayBtn.style.cursor = 'pointer';
          overlayBtn.style.opacity = '0';
          overlayBtn.style.pointerEvents = 'none';
          overlayBtn.style.transition = 'opacity 120ms ease';

          this.el.style.position = 'relative';
          this.el.appendChild(overlayBtn);

          overlayBtn.addEventListener('click', (event) => {
            this.openRawHtmlEditor(event);
          });
        },
        showEditorOverlay() {
          const overlay = this.el.querySelector('[data-raw-html-editor-overlay="true"]');
          if (!overlay) return;
          overlay.style.opacity = '1';
          overlay.style.pointerEvents = 'auto';
        },
        hideEditorOverlay() {
          const overlay = this.el.querySelector('[data-raw-html-editor-overlay="true"]');
          if (!overlay) return;
          overlay.style.opacity = '0';
          overlay.style.pointerEvents = 'none';
        },
      },
      isComponent: (el) => el && el.classList && el.classList.contains('gjs-raw-html-block'),
    });

    editor.BlockManager.add('raw-html-block', {
      label: 'Raw HTML',
      category: 'Basic',
      content: {
        type: 'raw-html',
        rawHtml: '',
      },
      media: '<span class="gjs-fonts gjs-f-text">HTML</span>',
    });

    // Form webhooks: add traits so each form can set abandoned-cart and lead-submit webhook URLs
    const formType = editor.DomComponents.getType('form');
    const baseTraits = Array.isArray(formType?.model?.prototype?.defaults?.traits)
      ? [...formType.model.prototype.defaults.traits]
      : [];
    editor.DomComponents.addType('form', {
      extend: 'form',
      model: {
        defaults: {
          traits: [
            ...baseTraits,
            { type: 'text', name: 'data-abandoned-cart-webhook', label: 'Abandoned cart webhook', placeholder: 'https://...' },
            { type: 'text', name: 'data-lead-submit-webhook', label: 'Lead submit webhook', placeholder: 'https://...' },
          ],
        },
      },
    });

    // Button URL: add a URL trait so non-submit buttons can navigate on click
    const buttonType = editor.DomComponents.getType('button');
    const baseButtonTraits = Array.isArray(buttonType?.model?.prototype?.defaults?.traits)
      ? [...buttonType.model.prototype.defaults.traits]
      : [];
    const parentButtonInit = buttonType?.model?.prototype?.init;
    const parentButtonViewInit = buttonType?.view?.prototype?.init;
    const escapeButtonOnclickUrl = (url) =>
      String(url || '').trim().replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    const isButtonUrlNewTab = (value) => {
      if (value == null || value === false) return false;
      return String(value).toLowerCase() !== 'false';
    };
    const mergeInlineStyleAttr = (styleAttr, prop, value) => {
      const decls = new Map();
      String(styleAttr || '')
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => {
          const idx = part.indexOf(':');
          if (idx === -1) return;
          const key = part.slice(0, idx).trim().toLowerCase();
          const val = part.slice(idx + 1).trim();
          if (key) decls.set(key, val);
        });
      const propKey = String(prop || '').toLowerCase();
      if (value == null || value === '') decls.delete(propKey);
      else decls.set(propKey, value);
      if (decls.size === 0) return '';
      return Array.from(decls.entries())
        .map(([key, val]) => `${key}: ${val}`)
        .join('; ');
    };
    const getButtonLinkState = (model) => {
      const url = String(model.get('buttonUrl') || '').trim();
      const btnType = (model.getAttributes() || {}).type || 'button';
      const isLinkButton = Boolean(url && btnType === 'button');
      const openNewTab = isLinkButton && Boolean(model.get('buttonUrlNewTab'));
      return { url, isLinkButton, openNewTab };
    };
    const applyButtonLinkPresentation = (model) => {
      const { url, isLinkButton, openNewTab } = getButtonLinkState(model);
      const attrs = { ...(model.getAttributes() || {}) };

      if (url) attrs['data-button-url'] = url;
      else delete attrs['data-button-url'];

      if (openNewTab) attrs['data-button-url-new-tab'] = 'true';
      else delete attrs['data-button-url-new-tab'];

      if (isLinkButton) {
        const safeUrl = escapeButtonOnclickUrl(url);
        attrs.onclick = openNewTab
          ? `window.open('${safeUrl}','_blank','noopener,noreferrer')`
          : `window.location.href='${safeUrl}'`;
      } else {
        delete attrs.onclick;
      }

      attrs.style = mergeInlineStyleAttr(attrs.style, 'cursor', isLinkButton ? 'pointer' : null);
      model.setAttributes(attrs);

      if (isLinkButton) {
        model.addClass('gjs-button-has-url');
        model.addStyle({ cursor: 'pointer' });
      } else {
        model.removeClass('gjs-button-has-url');
        const style = { ...(model.getStyle() || {}) };
        delete style.cursor;
        model.setStyle(style);
      }
    };
    editor.DomComponents.addType('button', {
      extend: 'button',
      model: {
        defaults: {
          buttonUrl: '',
          buttonUrlNewTab: false,
          traits: [
            ...baseButtonTraits,
            {
              type: 'text',
              name: 'buttonUrl',
              label: 'URL',
              changeProp: 1,
              placeholder: 'https://...',
            },
            {
              type: 'checkbox',
              name: 'buttonUrlNewTab',
              label: 'Open in new tab',
              changeProp: 1,
            },
          ],
        },
        init() {
          if (typeof parentButtonInit === 'function') {
            parentButtonInit.call(this);
          }
          const attrs = this.getAttributes() || {};
          if (!this.get('buttonUrl') && attrs['data-button-url']) {
            this.set('buttonUrl', attrs['data-button-url'], { silent: true });
          }
          if (!this.get('buttonUrlNewTab') && isButtonUrlNewTab(attrs['data-button-url-new-tab'])) {
            this.set('buttonUrlNewTab', true, { silent: true });
          }
          this.on('change:buttonUrl change:buttonUrlNewTab change:attributes:type', this.syncButtonUrl);
          this.syncButtonUrl();
        },
        syncButtonUrl() {
          applyButtonLinkPresentation(this);
        },
      },
      view: {
        init() {
          if (typeof parentButtonViewInit === 'function') {
            parentButtonViewInit.call(this);
          }
          this.listenTo(
            this.model,
            'change:buttonUrl change:buttonUrlNewTab change:attributes:type change:classes change:attributes:style',
            this.applyLinkButtonCursor
          );
        },
        onRender() {
          this.applyLinkButtonCursor();
        },
        applyLinkButtonCursor() {
          if (!this.el) return;
          const { isLinkButton } = getButtonLinkState(this.model);
          if (isLinkButton) {
            this.el.style.setProperty('cursor', 'pointer', 'important');
          } else {
            this.el.style.removeProperty('cursor');
          }
        },
      },
    });
    editor.addStyle(`
      button.gjs-button-has-url {
        cursor: pointer !important;
      }
      button.gjs-button-has-url:hover {
        filter: brightness(1.08);
      }
      button.gjs-button-has-url:active {
        filter: brightness(0.95);
      }
    `);
    const BUTTON_LINK_STYLE_ID = 'gjs-button-link-styles';
    const injectButtonLinkStylesIntoCanvas = () => {
      const doc = editor.Canvas.getDocument?.();
      if (!doc) return;
      let el = doc.getElementById(BUTTON_LINK_STYLE_ID);
      if (!el) {
        el = doc.createElement('style');
        el.id = BUTTON_LINK_STYLE_ID;
        (doc.head || doc.body).appendChild(el);
      }
      el.textContent = `
        button.gjs-button-has-url {
          cursor: pointer !important;
        }
        button.gjs-button-has-url:hover {
          filter: brightness(1.08);
        }
        button.gjs-button-has-url:active {
          filter: brightness(0.95);
        }
      `;
    };
    const syncAllButtonLinks = (component) => {
      if (!component) return;
      if (component.get('type') === 'button' && typeof component.syncButtonUrl === 'function') {
        component.syncButtonUrl();
      }
      const children = component.get('components');
      if (children && children.length > 0) {
        children.each((child) => syncAllButtonLinks(child));
      }
    };
    editor.on('load', () => {
      injectButtonLinkStylesIntoCanvas();
      editor.getComponents().each((component) => syncAllButtonLinks(component));
    });
    editor.on('component:add', (component) => {
      if (component?.get?.('type') === 'button' && typeof component.syncButtonUrl === 'function') {
        component.syncButtonUrl();
      }
    });

    // Ensure editor starts empty
    editor.setComponents('');
    editor.setStyle('');

    setBackgroundImagePickerHandler((currentUrl, onSelect, options = {}) => {
      setBgImagePickerCurrentUrl(currentUrl || '');
      setBgImagePickerTitle(options.title || 'Background image');
      bgImagePickerOnSelectRef.current = onSelect;
      setBgImagePickerOpen(true);
    });
    registerBackgroundImageProperty(editor);
    registerCustomCssProperty(editor);
    registerDeviceVisibilityProperty(editor);

    // Route the built-in 'open-assets' command (image src trait, double-click on image,
    // etc.) through our shared image picker instead of GrapesJS' Asset Manager.
    const originalOpenAssets = editor.Commands.get('open-assets');
    editor.Commands.add('open-assets', {
      run(ed, sender, opts = {}) {
        const target = opts.target || ed.getSelected();
        let currentUrl = '';
        if (target && typeof target.get === 'function') {
          const src = target.get('src');
          if (typeof src === 'string' && src.trim() !== '') {
            currentUrl = src;
          }
        }
        if (!currentUrl && typeof opts.value === 'string') {
          currentUrl = extractUrlFromBackgroundImage(opts.value) || opts.value;
        }

        const handleSelect = (url) => {
          if (!url) return;
          if (typeof opts.select === 'function') {
            const fakeAsset = {
              get: (key) => (key === 'src' || key === 'url' ? url : ''),
              getSrc: () => url,
              attributes: { src: url, type: 'image' },
            };
            opts.select(fakeAsset, true);
          } else if (target && typeof target.set === 'function') {
            target.set('src', url);
          }
        };

        const opened = requestOpenBackgroundImagePicker(currentUrl, handleSelect, {
          title: 'Image',
        });

        if (!opened && originalOpenAssets && typeof originalOpenAssets.run === 'function') {
          // Fall back to the default Asset Manager if our picker isn't ready yet.
          return originalOpenAssets.run.call(this, ed, sender, opts);
        }
        return undefined;
      },
      stop(ed, sender, opts = {}) {
        if (originalOpenAssets && typeof originalOpenAssets.stop === 'function') {
          try {
            originalOpenAssets.stop.call(this, ed, sender, opts);
          } catch (_) {
            /* ignore */
          }
        }
      },
    });

    // Configure Style Manager to properly handle background-image
    const styleManager = editor.StyleManager;
    const PARALLAX_ENABLED_PROP = '--gjs-parallax';
    const PARALLAX_SPEED_PROP = '--gjs-parallax-speed';
    // Responsive visibility lives in the General sector as a custom
    // "device-visibility" Style Manager property (see
    // registerDeviceVisibilityProperty). It writes data-gjs-hide-* attributes
    // directly on the selected component — device-independent, so it stays
    // reliable regardless of the active canvas device — and the managed
    // media-query CSS (buildDeviceVisibilityCss) does the hiding.
    
    // Get or create background sector
    const sectors = styleManager.getSectors();
    let backgroundSector = sectors.find(sector => sector.getName() === 'background');
    
    if (!backgroundSector) {
      // Create background sector if it doesn't exist
      backgroundSector = styleManager.addSector('background', {
        name: 'Background',
        open: false,
      });
    }

    // Position controls for free placement and per-device adjustments.
    const sectorsById = styleManager.getSectors();
    let positionSector = sectorsById.find((sector) => sector.getId && sector.getId() === 'position');
    if (!positionSector) {
      positionSector = styleManager.addSector('position', {
        name: 'Position',
        open: true,
      });
    }

    const positionProps = positionSector.getProperties();
    const positionProperties = [
      {
        name: 'position',
        property: 'position',
        type: 'select',
        defaults: 'relative',
        options: [
          { value: 'static', name: 'Static' },
          { value: 'relative', name: 'Relative' },
          { value: 'absolute', name: 'Absolute' },
          { value: 'fixed', name: 'Fixed' },
        ],
      },
      { name: 'top', property: 'top', type: 'number', units: ['px', '%', 'vh'], defaults: '' },
      { name: 'right', property: 'right', type: 'number', units: ['px', '%', 'vw'], defaults: '' },
      { name: 'bottom', property: 'bottom', type: 'number', units: ['px', '%', 'vh'], defaults: '' },
      { name: 'left', property: 'left', type: 'number', units: ['px', '%', 'vw'], defaults: '' },
      { name: 'z-index', property: 'z-index', type: 'integer', defaults: '' },
    ];

    positionProperties.forEach((propConfig) => {
      const existingProp = positionProps.find((prop) => {
        const propName = prop.getName ? prop.getName() : prop.get('name');
        const cssProperty = prop.get ? prop.get('property') : null;
        return propName === propConfig.name || cssProperty === propConfig.property;
      });

      if (!existingProp) {
        const propToAdd = {
          name: propConfig.name,
          property: propConfig.property,
          type: propConfig.type,
          defaults: propConfig.defaults,
        };
        if (propConfig.options) propToAdd.options = propConfig.options;
        if (propConfig.units) propToAdd.units = propConfig.units;
        positionSector.addProperty(propToAdd);
      }
    });

    // General sector: responsive visibility ("Hide on desktop/tablet/mobile")
    // and the Custom CSS editor.
    const sectorListForGeneral = Array.isArray(sectors) ? sectors : sectors?.models || [];
    const generalSector = sectorListForGeneral.find(
      (sector) =>
        (sector.getId && sector.getId() === 'general') ||
        (sector.get && sector.get('id') === 'general') ||
        (sector.getName && sector.getName() === 'General')
    );
    if (generalSector) {
      const visibilityPropConfig = {
        id: 'device-visibility',
        name: 'Visibility',
        type: 'device-visibility',
        full: true,
        className: 'gjs-sm-property-device-visibility',
      };
      const generalPropsForVis = generalSector.getProperties();
      const generalPropsForVisList = Array.isArray(generalPropsForVis)
        ? generalPropsForVis
        : generalPropsForVis?.models || [];
      const existingVisibility = generalPropsForVisList.find((prop) => {
        const propType = prop.getType ? prop.getType() : prop.get('type');
        const propId = prop.getId ? prop.getId() : prop.get('id');
        return propType === visibilityPropConfig.type || propId === visibilityPropConfig.id;
      });
      if (!existingVisibility) {
        generalSector.addProperty(visibilityPropConfig);
      }

      const customCssPropConfig = {
        id: 'custom-css',
        name: 'Custom CSS',
        type: 'custom-css-editor',
        full: true,
        className: 'gjs-sm-property-custom-css',
      };
      const propsAfterVisibility = generalSector.getProperties();
      const propsAfterList = Array.isArray(propsAfterVisibility)
        ? propsAfterVisibility
        : propsAfterVisibility?.models || [];
      const existingCustomCss = propsAfterList.find((prop) => {
        const propName = prop.getName ? prop.getName() : prop.get('name');
        const propType = prop.getType ? prop.getType() : prop.get('type');
        return propName === customCssPropConfig.name || propType === customCssPropConfig.type;
      });
      if (existingCustomCss) {
        const cssProperty = existingCustomCss.get
          ? existingCustomCss.get('property')
          : existingCustomCss.property;
        if (cssProperty === 'custom-css-editor' || cssProperty === '--gjs-custom-css') {
          generalSector.removeProperty(existingCustomCss);
          generalSector.addProperty(customCssPropConfig);
        }
      } else {
        generalSector.addProperty(customCssPropConfig);
      }
    }

    const reconcileManagedResponsiveCss = () => {
      const currentCss = editor.getCss?.() || '';
      const needsLegacyMigration = cssNeedsLegacyColumnMigration(currentCss);
      const missingManagedBlocks = MANAGED_CSS_MARKERS.some(
        (marker) => !currentCss.includes(marker),
      );
      // A device-visibility block from before the trait refactor won't contain
      // the data-gjs-hide-* selector; strip it so the current builder re-injects.
      const staleVisibilityBlock =
        currentCss.includes('gjs-device-visibility') &&
        !currentCss.includes('data-gjs-hide-desktop');

      if (needsLegacyMigration || staleVisibilityBlock) {
        let css = currentCss;
        MANAGED_CSS_MARKERS.forEach((marker) => {
          css = stripManagedCssBlock(css, marker);
        });
        if (needsLegacyMigration) css = migrateLegacyColumnCss(css);
        editor.setStyle(css);
        ensureAllComponentSelectors(editor);
      } else if (!missingManagedBlocks) {
        // Managed global CSS is already present — avoid setStyle(), which
        // re-parses every component rule and can break selector collections
        // when rows/cells carry background-image id rules.
        return;
      }

      if (!editor.getCss().includes('gjs-device-visibility')) {
        editor.addStyle(buildDeviceVisibilityCss());
      }
      if (!editor.getCss().includes('gjs-blocks-basic-columns')) {
        editor.addStyle(buildColumnLayoutCss());
      }
      if (!editor.getCss().includes('gjs-row-layout')) {
        editor.addStyle(buildRowLayoutCss());
      }
    };
    reconcileManagedResponsiveCss();

    // Guard Style Manager / Selector Manager when selector collections are stale.
    const selectorManager = editor.SelectorManager;
    if (selectorManager && typeof selectorManager.__getCommonSelectors === 'function') {
      const origGetCommonSelectors = selectorManager.__getCommonSelectors.bind(selectorManager);
      selectorManager.__getCommonSelectors = (components, opts) => {
        const safe = (components || []).filter((cmp) => {
          if (!cmp || typeof cmp.getSelectors !== 'function') return false;
          ensureComponentSelectors(cmp);
          const sels = cmp.getSelectors();
          return sels && typeof sels.getValid === 'function';
        });
        return origGetCommonSelectors(safe, opts);
      };
    }
    const styleManagerApi = editor.Styles;
    if (styleManagerApi && typeof styleManagerApi.getParentRules === 'function') {
      const origGetParentRules = styleManagerApi.getParentRules.bind(styleManagerApi);
      styleManagerApi.getParentRules = (target, opts = {}) => {
        const component = opts.component;
        if (component) ensureComponentSelectors(component);
        if (target && typeof target.getSelectors === 'function') {
          const sels = target.getSelectors();
          if (!sels || typeof sels.getFullName !== 'function') {
            return [];
          }
        }
        try {
          return origGetParentRules(target, opts);
        } catch (err) {
          console.warn('[WebBuilder] getParentRules failed; returning no parent rules.', err);
          return [];
        }
      };
    }

    const syncParallaxAttrsFromStyle = (component) => {
      if (!component || typeof component.getStyle !== 'function') return;
      const style = component.getStyle() || {};
      const current = component.getAttributes?.() || {};
      const rawEnabled = style[PARALLAX_ENABLED_PROP];
      const enabled = String(rawEnabled ?? '').trim() === '1';
      const speedNum = parseFloat(style[PARALLAX_SPEED_PROP]);
      const speed = Number.isFinite(speedNum) && speedNum > 0 ? speedNum : 0.3;
      const next = {
        'data-gjs-parallax': enabled ? '1' : '0',
        'data-gjs-parallax-speed': String(speed),
      };
      const changed =
        String(current['data-gjs-parallax'] ?? '') !== next['data-gjs-parallax'] ||
        String(current['data-gjs-parallax-speed'] ?? '') !== next['data-gjs-parallax-speed'];
      if (changed) component.addAttributes(next);
    };

    const syncParallaxAttrsRecursive = (component) => {
      if (!component) return;
      syncParallaxAttrsFromStyle(component);
      const children = component.get('components');
      if (children && children.length > 0) {
        children.each((child) => syncParallaxAttrsRecursive(child));
      }
    };

    editor.__reconcileManagedResponsiveCss = reconcileManagedResponsiveCss;
    editor.__ensureDeviceVisibilityCss = reconcileManagedResponsiveCss;
    editor.__syncAllParallax = () => {
      editor.getComponents().each((c) => syncParallaxAttrsRecursive(c));
    };
    editor.__syncAllCustomCss = () => {
      editor.getComponents().each((c) => syncCustomCssRecursive(c, editor));
    };
    
    // Ensure all background properties exist and are properly configured
    const bgProps = backgroundSector.getProperties();
    const backgroundProperties = [
      { name: 'background-image', type: 'background-image-picker', property: 'background-image' },
      { name: 'background-repeat', type: 'select', options: [
        { value: 'repeat', name: 'Repeat' },
        { value: 'no-repeat', name: 'No Repeat' },
        { value: 'repeat-x', name: 'Repeat X' },
        { value: 'repeat-y', name: 'Repeat Y' },
        { value: 'space', name: 'Space' },
        { value: 'round', name: 'Round' },
      ]},
      { name: 'background-position', type: 'text', placeholder: 'e.g., center, top left, 50% 50%' },
      { name: 'background-size', type: 'select', options: [
        { value: 'auto', name: 'Auto' },
        { value: 'cover', name: 'Cover' },
        { value: 'contain', name: 'Contain' },
        { value: '100% 100%', name: '100% 100%' },
      ]},
      { name: 'background-attachment', type: 'select', options: [
        { value: 'scroll', name: 'Scroll' },
        { value: 'fixed', name: 'Fixed' },
        { value: 'local', name: 'Local' },
      ]},
      { name: 'background-color', type: 'color' },
      {
        name: 'Parallax',
        property: PARALLAX_ENABLED_PROP,
        type: 'radio',
        defaults: '0',
        list: [
          { value: '0', name: 'Off' },
          { value: '1', name: 'On' },
        ],
      },
      {
        name: 'Parallax Speed',
        property: PARALLAX_SPEED_PROP,
        type: 'number',
        min: 0.05,
        max: 2,
        step: 0.05,
        defaults: 0.3,
      },
    ];
    
    backgroundProperties.forEach(propConfig => {
      let existingProp = bgProps.find(prop => {
        const propName = prop.getName ? prop.getName() : prop.get('name');
        const cssProperty = prop.get ? prop.get('property') : null;
        return propName === propConfig.name || cssProperty === propConfig.property;
      });
      
      // Remove existing property if it's the wrong type.
      if (existingProp && propConfig.name === 'background-image') {
        // Force a single canonical background-image property registration.
        backgroundSector.removeProperty(existingProp);
        existingProp = null;
      }

      if (existingProp && propConfig.name === 'Parallax') {
        const propType = existingProp.getType ? existingProp.getType() : existingProp.get('type');
        if (propType !== 'radio') {
          backgroundSector.removeProperty(existingProp);
          existingProp = null;
        }
      }
      
      // Add property if it doesn't exist
      if (!existingProp) {
        const propToAdd = {
          name: propConfig.name,
          property: propConfig.property || propConfig.name,
          type: propConfig.type,
          defaults: '',
        };
        
        if (propConfig.placeholder) {
          propToAdd.placeholder = propConfig.placeholder;
        }
        
        if (propConfig.options) {
          propToAdd.options = propConfig.options;
        }

        if (propConfig.functionName) {
          propToAdd.functionName = propConfig.functionName;
        }

        if (propConfig.valueTrue !== undefined) {
          propToAdd.valueTrue = propConfig.valueTrue;
        }

        if (propConfig.valueFalse !== undefined) {
          propToAdd.valueFalse = propConfig.valueFalse;
        }

        if (propConfig.list) {
          propToAdd.list = propConfig.list;
        }

        if (propConfig.min !== undefined) {
          propToAdd.min = propConfig.min;
        }

        if (propConfig.max !== undefined) {
          propToAdd.max = propConfig.max;
        }

        if (propConfig.step !== undefined) {
          propToAdd.step = propConfig.step;
        }
        
        backgroundSector.addProperty(propToAdd);
      }
    });

    const normalizeStylePropertyValue = (propertyName, value) => {
      if (propertyName === 'background-image') {
        const base = stripOverlayGradientFromBackgroundImage(value);
        // Leave gradient-only / non-image values intact; only image URLs get
        // normalized into url("…") form.
        if (!extractUrlFromBackgroundImage(base)) return value;
        return normalizeBackgroundImageValue(base);
      }
      return value;
    };

    const getComponentClassNames = (component) => {
      if (!component) return [];
      const classesCollection = component.getClasses ? component.getClasses() : [];
      const classNamesFromCollection = Array.isArray(classesCollection)
        ? classesCollection
            .map((cls) => {
              if (typeof cls === 'string') return cls;
              if (cls && typeof cls.getName === 'function') return cls.getName();
              return '';
            })
            .filter(Boolean)
        : [];
      const attrs = component.getAttributes ? component.getAttributes() : {};
      const attrsClass = typeof attrs.class === 'string' ? attrs.class.split(/\s+/).filter(Boolean) : [];
      return [...classNamesFromCollection, ...attrsClass].map((name) => String(name).toLowerCase());
    };

    const COLUMN_BLOCK_IDS = ['column1', 'column2', 'column3', 'column3-7'];

    // grapesjs-blocks-basic renders columns as <div class="gjs-row"> wrappers
    // containing <div class="gjs-cell"> children (stylePrefix "gjs-"). The
    // dropped components do NOT carry an HTML id of "column1" etc., so the row
    // and cell classes are what actually identify a column-like element.
    const COLUMN_LIKE_CLASS_NAMES = ['gjs-row', 'gjs-cell', 'row', 'cell', ];

    const isColumnLikeComponent = (component) => {
      if (!component) return false;
      const classNames = getComponentClassNames(component);
      return classNames.some((name) => COLUMN_LIKE_CLASS_NAMES.includes(name));
    };

    const isRowComponent = (component) => {
      if (!component) return false;
      const classNames = getComponentClassNames(component);
      return classNames.some((name) => name === 'gjs-row' || name === 'row');
    };

    let rowLayoutSector = styleManager.getSector('row-layout');
    if (!rowLayoutSector) {
      rowLayoutSector = styleManager.addSector('row-layout', {
        name: 'Row layout',
        open: true,
        visible: false,
      });
    }

    const rowLayoutProperties = [
      {
        name: 'Gutter width',
        property: ROW_GUTTER_PROP,
        type: 'number',
        units: ['px'],
        defaults: '',
      },
      {
        name: 'Max width (desktop)',
        property: ROW_MAX_DESKTOP_PROP,
        type: 'number',
        units: ['px', '%'],
        defaults: '',
      },
      {
        name: 'Max width (tablet)',
        property: ROW_MAX_TABLET_PROP,
        type: 'number',
        units: ['px', '%'],
        defaults: '',
      },
      {
        name: 'Max width (mobile)',
        property: ROW_MAX_MOBILE_PROP,
        type: 'number',
        units: ['px', '%'],
        defaults: '',
      },
    ];

    const rowLayoutProps = rowLayoutSector.getProperties();
    const rowLayoutPropList = Array.isArray(rowLayoutProps) ? rowLayoutProps : rowLayoutProps?.models || [];
    rowLayoutProperties.forEach((propConfig) => {
      const existingProp = rowLayoutPropList.find((prop) => {
        const propName = prop.getName ? prop.getName() : prop.get('name');
        const cssProperty = prop.get ? prop.get('property') : null;
        return propName === propConfig.name || cssProperty === propConfig.property;
      });
      if (!existingProp) {
        rowLayoutSector.addProperty({
          name: propConfig.name,
          property: propConfig.property,
          type: propConfig.type,
          defaults: propConfig.defaults,
          units: propConfig.units,
        });
      }
    });

    const updateRowLayoutSectorVisibility = () => {
      const sector = styleManager.getSector('row-layout');
      if (!sector) return;
      const selected = editor.getSelected();
      sector.set('visible', isRowComponent(selected));
    };

    const hasColumnLikeDescendant = (component) => {
      if (!component) return false;
      const children = component.get('components');
      if (!children || children.length === 0) return false;
      let found = false;
      children.each((child) => {
        if (found) return;
        if (isColumnLikeComponent(child) || hasColumnLikeDescendant(child)) {
          found = true;
        }
      });
      return found;
    };

    // When the user mousedowns the small "absolute" button on a block button
    // in the BlockManager, the next block drag should drop the component using
    // absolute positioning. Otherwise, blocks default to translate (in-flow).
    let pendingAbsoluteBlockDrag = false;

    // Clearing the drag mode (empty/unset) makes GrapesJS use its default
    // sorter for moves, which keeps elements in normal flow and lets them be
    // dragged between containers/columns. Setting 'translate' or 'absolute'
    // would switch to free-dragging, which prevents moving between columns.
    const setEditorDragModeForComponent = (component) => {
      if (!component) return;
      editor.setDragMode('');
    };

    const setEditorDragModeForBlockDrag = (component) => {
      editor.setDragMode('');
    };

    // Adds a small "absolute drag" button to the top-right of each non-column
    // block button in the BlockManager. Mousedown on the button sets a flag so
    // the next block drag drops the component with absolute positioning, while
    // dragging from anywhere else on the block uses translate (in-flow) mode.
    const ABS_DRAG_BTN_CLASS = 'gjs-block-abs-drag-btn';
    const ABS_DRAG_BTN_FLAG_KEY = '__absDragBtnWrapped';
    const ABS_DRAG_BTN_ORIG_KEY = '__absDragBtnOriginalRender';
    const ABS_DRAG_BTN_ICON = `
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M5 9V5h4"/>
        <path d="M19 9V5h-4"/>
        <path d="M5 15v4h4"/>
        <path d="M19 15v4h-4"/>
      </svg>
    `;

    const attachAbsoluteDragButton = (el) => {
      if (!el || el.querySelector(`.${ABS_DRAG_BTN_CLASS}`)) return;
      const btn = document.createElement('span');
      btn.className = ABS_DRAG_BTN_CLASS;
      btn.title = 'Drag with absolute positioning';
      btn.setAttribute('role', 'button');
      btn.setAttribute('aria-label', 'Drag with absolute positioning');
      btn.innerHTML = ABS_DRAG_BTN_ICON;
      btn.addEventListener('mousedown', (ev) => {
        if (ev.button !== 0) return;
        pendingAbsoluteBlockDrag = true;
        // Safety: if no drag actually happens (or block:drag:stop never
        // fires), make sure the flag does not leak into the next drag.
        const onUp = () => {
          document.removeEventListener('mouseup', onUp, true);
          requestAnimationFrame(() => {
            pendingAbsoluteBlockDrag = false;
          });
        };
        document.addEventListener('mouseup', onUp, true);
      });
      el.appendChild(btn);
    };

    const wrapBlockWithAbsoluteDragButton = (blockModel) => {
      if (!blockModel || !blockModel.id) return;
      if (COLUMN_BLOCK_IDS.includes(blockModel.id)) return;
      if (blockModel[ABS_DRAG_BTN_FLAG_KEY]) return;

      const originalRender = blockModel.get('render');
      blockModel[ABS_DRAG_BTN_ORIG_KEY] = originalRender;
      blockModel[ABS_DRAG_BTN_FLAG_KEY] = true;

      blockModel.set('render', (ctx) => {
        const orig = blockModel[ABS_DRAG_BTN_ORIG_KEY];
        if (typeof orig === 'function') {
          const out = orig(ctx);
          if (out) ctx.el.innerHTML = out;
        }
        attachAbsoluteDragButton(ctx.el);
        return null;
      });
    };

    const setupAllAbsoluteDragButtons = () => {
      editor.BlockManager.getAll().each((blockModel) => {
        wrapBlockWithAbsoluteDragButton(blockModel);
      });
    };

    const applyDefaultAbsolutePositioning = (component) => {
      if (!component || isColumnLikeComponent(component) || hasColumnLikeDescendant(component)) return;
      const currentStyle = component.getStyle?.() || {};
      const nextStyle = {};
      if (!currentStyle.position || currentStyle.position === 'static') {
       // nextStyle.position = 'absolute';
      }
      if (
        currentStyle.top === undefined &&
        currentStyle.bottom === undefined &&
        currentStyle.left === undefined &&
        currentStyle.right === undefined
      ) {
        nextStyle.top = '0px';
        nextStyle.left = '0px';
      }
      if (Object.keys(nextStyle).length > 0) {
        component.addStyle(nextStyle);
      }
    };

    const enableColumnVerticalResize = (component) => {
      if (!component || !isColumnLikeComponent(component)) return;
      const currentStyle = component.getStyle?.() || {};
      if (!currentStyle['min-height']) {
        component.addStyle({ 'min-height': '120px' });
      }
      component.set('resizable', {
        tl: 0,
        tc: 1,
        tr: 0,
        cl: 0,
        cr: 0,
        bl: 0,
        bc: 1,
        br: 0,
        keyHeight: 'height',
        minDim: 40,
      });
    };

    const setupCanvasParallaxPreview = () => {
      const frame = editor.Canvas.getFrameEl();
      if (!frame || !frame.contentWindow || !frame.contentDocument) {
        return () => {};
      }

      const frameWindow = frame.contentWindow;
      const frameDocument = frame.contentDocument;
      let frameRafId = null;

      const applyParallax = () => {
        frameRafId = null;
        const scrollTop = frameWindow.scrollY || frameDocument.documentElement.scrollTop || 0;
        const parallaxElements = frameDocument.querySelectorAll('[data-gjs-parallax="1"]');

        parallaxElements.forEach((el) => {
          const styles = frameWindow.getComputedStyle(el);
          const speedAttr = parseFloat(el.getAttribute('data-gjs-parallax-speed'));
          const speed = Number.isFinite(speedAttr) && speedAttr > 0 ? speedAttr : 0.3;

          const rect = el.getBoundingClientRect();
          const elementTop = rect.top + scrollTop;
          const offsetY = (scrollTop - elementTop) * speed;
          const positionX = styles.backgroundPositionX || 'center';
          el.style.backgroundPosition = `${positionX} ${offsetY}px`;
        });
      };

      const queueParallaxRender = () => {
        if (frameRafId !== null) return;
        frameRafId = frameWindow.requestAnimationFrame(applyParallax);
      };

      frameWindow.addEventListener('scroll', queueParallaxRender, { passive: true });
      frameWindow.addEventListener('resize', queueParallaxRender);
      queueParallaxRender();

      return () => {
        frameWindow.removeEventListener('scroll', queueParallaxRender);
        frameWindow.removeEventListener('resize', queueParallaxRender);
        if (frameRafId !== null) {
          frameWindow.cancelAnimationFrame(frameRafId);
          frameRafId = null;
        }
      };
    };

    let tearDownParallaxPreview = setupCanvasParallaxPreview();
    let parallaxPreviewRefreshTimer = null;
    const refreshParallaxPreview = () => {
      if (parallaxPreviewRefreshTimer) clearTimeout(parallaxPreviewRefreshTimer);
      parallaxPreviewRefreshTimer = setTimeout(() => {
        parallaxPreviewRefreshTimer = null;
        tearDownParallaxPreview();
        tearDownParallaxPreview = setupCanvasParallaxPreview();
      }, 100);
    };
    const handleStylePropertyUpdate = (propertyModel) => {
      const selected = editor.getSelected();
      if (!selected || !propertyModel) return;

      const propAttrs =
        (propertyModel.property && propertyModel.property.attributes) || {};
      // `id` defaults to the `name` field ('Parallax'); the CSS property is `property`.
      const propertyName = propAttrs.property || propAttrs.id || null;
      if (!propertyName) return;

      let nextValue = '';
      if (typeof propertyModel.getFullValue === 'function') {
        nextValue = propertyModel.getFullValue();
      } else if (typeof propertyModel.getValue === 'function') {
        nextValue = propertyModel.getValue();
      } else if (propertyModel.get) {
        nextValue = propertyModel.get('value');
      }

      if (ROW_LAYOUT_STYLE_KEYS.includes(propertyName) && isRowComponent(selected)) {
        const normalizedValue = normalizeRowLayoutStyleValue(propertyName, nextValue);
        if (normalizedValue === '') {
          selected.removeStyle(propertyName);
        } else {
          selected.addStyle({ [propertyName]: normalizedValue });
        }
        return;
      }

      const isParallaxProp =
        propertyName === PARALLAX_ENABLED_PROP ||
        propertyName === PARALLAX_SPEED_PROP;
      const isBackgroundImageOrColor =
        propertyName === 'background-image' || propertyName === 'background-color';

      if (!isParallaxProp && !isBackgroundImageOrColor) return;

      const normalizedValue = normalizeStylePropertyValue(propertyName, nextValue);
      const trimmed = String(normalizedValue ?? '').trim();
      // Style Manager re-renders on selection and can emit empty values for
      // background-* fields; writing those would clear background-size: cover
      // and make the image vanish while the URL remains in the picker.
      if (trimmed === '') return;

      // Shapes store background-* in per-instance CSS rules; writing on selection
      // re-triggers migrate/strip loops.
      if (isBackgroundImageOrColor && isShapeComponent(selected)) return;

      selected.addStyle({
        [propertyName]: normalizedValue,
      });

      if (isBackgroundImageOrColor && !isShapeComponent(selected)) {
        reconcileBackgroundImageOverlay(selected);
      }

      if (isParallaxProp) {
        syncParallaxAttrsFromStyle(selected);
      }
    };
    const syncSelectedParallax = () => {
      const selected = editor.getSelected();
      if (selected) {
        syncParallaxAttrsFromStyle(selected);
      }
    };
    const handleBlockDragStart = (component) => {
      setEditorDragModeForBlockDrag(component);
    };
    const handleBlockDragStop = (component) => {
      // Translate-only mode: components are always left wherever the translate
      // sorter inserted them (in document flow); never force absolute.
      enableColumnVerticalResize(component);
      if (isShapeComponent(component)) {
        finalizeShapeComponent(component);
      }
      pendingAbsoluteBlockDrag = false;
    };

    const handleCanvasFrameLoad = () => {
      injectShapeDefaultsIntoCanvas();
      editor.__syncAllParallax?.();
      editor.__syncAllCustomCss?.();
      refreshParallaxPreview();
      editor.getComponents().each((component) => {
        reconcileBackgroundImageOverlayRecursive(component);
      });
    };

    editor.on('canvas:frame:load', handleCanvasFrameLoad);
    editor.on('style:property:update', refreshParallaxPreview);
    // Guard against re-entrancy: migrate -> stripShapeComponentStyleProps ->
    // removeStyle fires another component:styleUpdate, which would call migrate
    // again. Without this guard that re-entry loops forever (stack overflow).
    const migratingShapeStyles = new Set();
    const handleShapeStyleUpdate = (component) => {
      if (!component || !isShapeComponent(component)) return;
      const key = component.cid || component;
      if (migratingShapeStyles.has(key)) return;
      migratingShapeStyles.add(key);
      try {
        migrateShapeComponentStylesToInstanceRule(component);
      } finally {
        migratingShapeStyles.delete(key);
      }
    };
    // Reconcile the tint overlay / background-blend-mode whenever the
    // background color or image changes on a component through ANY path
    // (Style Manager, the custom image picker, or programmatic addStyle).
    // `style:property:update` only fires for Style Manager UI edits, so it
    // misses some color changes; this model-level hook is the reliable signal.
    // A reentrancy guard prevents the addStyle() calls inside reconcile from
    // re-triggering this handler in a loop.
    const reconcilingBackgroundOverlay = new Set();
    const handleBackgroundStyleUpdate = (component) => {
      if (!component || isShapeComponent(component)) return;
      const key = component.cid || component;
      if (reconcilingBackgroundOverlay.has(key)) return;
      reconcilingBackgroundOverlay.add(key);
      try {
        reconcileBackgroundImageOverlay(component);
      } finally {
        reconcilingBackgroundOverlay.delete(key);
      }
    };
    editor.on('component:styleUpdate', handleShapeStyleUpdate);
    editor.on('component:styleUpdate', syncSelectedParallax);
    editor.on('component:styleUpdate:background-color', handleBackgroundStyleUpdate);
    editor.on('component:styleUpdate:background-image', handleBackgroundStyleUpdate);
    editor.on('style:property:update', handleStylePropertyUpdate);
    editor.on('style:property:update', syncSelectedParallax);
    editor.on('block:drag:stop', handleBlockDragStop);
    editor.on('block:drag:start', handleBlockDragStart);
    const handleComponentAddCustomCss = (cmp) => {
      syncCustomCssRecursive(cmp, editor);
    };
    editor.on('component:add', enableColumnVerticalResize);
    editor.on('component:add', syncParallaxAttrsFromStyle);
    editor.on('component:add', handleComponentAddCustomCss);
    editor.on('component:selected', setEditorDragModeForComponent);
    editor.on('component:selected', updateRowLayoutSectorVisibility);
    editor.on('component:deselected', updateRowLayoutSectorVisibility);

    // Apply resize handles to already-loaded column blocks
    const applyResizeRecursively = (component) => {
      if (!component) return;
      enableColumnVerticalResize(component);
      const children = component.get('components');
      if (children && children.length > 0) {
        children.each((child) => applyResizeRecursively(child));
      }
    };
    editor.getComponents().each((component) => applyResizeRecursively(component));
    updateRowLayoutSectorVisibility();
    editor.getComponents().each((component) => syncParallaxAttrsRecursive(component));
    editor.getComponents().each((component) => syncCustomCssRecursive(component, editor));
    editor.__finalizeAllShapes?.();
    const initiallySelected = editor.getSelected();
    if (initiallySelected) {
      setEditorDragModeForComponent(initiallySelected);
    }
    
    // Finalize legacy shapes first so inline styles migrate before other handlers run.
    editor.on('component:selected', (component) => {
      ensureComponentSelectors(component);
      if (isShapeComponent(component)) {
        finalizeShapeComponent(component);
      }
      // Defer so we don't call addStyle() while GrapesJS is still updating
      // selection / Style Manager targets (avoids selector collection races).
      setTimeout(() => {
        const selectedAll =
          typeof editor.getSelectedAll === 'function' ? editor.getSelectedAll() : [];
        const stillSelected = Array.isArray(selectedAll)
          ? selectedAll.includes(component)
          : editor.getSelected() === component;
        if (!stillSelected) return;
        extractBackgroundPropertiesFromStyles(component);
        syncParallaxAttrsFromStyle(component);
      }, 0);
    });

    const tearDownMarqueeSelection = setupMarqueeSelection(editor);
    const tearDownGroupDrag = setupGroupDrag(editor);
    const tearDownLayerContextMenu = setupLayerContextMenu(editor);

    editorRef.current = editor;

    // Cleanup
    return () => {
      clearBackgroundImagePickerHandler();
      tearDownMarqueeSelection();
      tearDownGroupDrag();
      tearDownLayerContextMenu();
      tearDownParallaxPreview();
      if (parallaxPreviewRefreshTimer) {
        clearTimeout(parallaxPreviewRefreshTimer);
        parallaxPreviewRefreshTimer = null;
      }
      editor.off('canvas:frame:load', handleCanvasFrameLoad);
      editor.off('style:property:update', refreshParallaxPreview);
      editor.off('component:styleUpdate', handleShapeStyleUpdate);
      editor.off('component:styleUpdate', syncSelectedParallax);
      editor.off('component:styleUpdate:background-color', handleBackgroundStyleUpdate);
      editor.off('component:styleUpdate:background-image', handleBackgroundStyleUpdate);
      editor.off('style:property:update', handleStylePropertyUpdate);
      editor.off('style:property:update', syncSelectedParallax);
      editor.off('block:drag:stop', handleBlockDragStop);
      editor.off('block:drag:start', handleBlockDragStart);
      editor.off('block:add', wrapBlockWithAbsoluteDragButton);
      editor.off('component:add', enableColumnVerticalResize);
      editor.off('component:add', syncParallaxAttrsFromStyle);
      editor.off('component:add', handleShapeComponentAdd);
      editor.off('component:selected', setEditorDragModeForComponent);
      editor.off('component:selected', updateRowLayoutSectorVisibility);
      editor.off('component:deselected', updateRowLayoutSectorVisibility);
      if (editor) {
        // Clear editor content before destroying
        editor.setComponents('');
        editor.setStyle('');
        editor.destroy();
      }
      // Clear the container element
      const container = document.getElementById('gjs');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [websiteId]);

  // Track unsaved changes when editor changes
  useEffect(() => {
    if (!editorRef.current || !selectedAudience || !websiteId || isInitialLoad) return;

    const editor = editorRef.current;

    // Track changes to detect unsaved modifications
    const handleUpdate = () => {
      if (isInitialLoad) return;
      
      // Use a small delay to debounce rapid updates
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        const html = editor.getHtml();
        const css = editor.getCss();
        
        // Normalize content for comparison (trim whitespace)
        const normalizedHtml = html ? html.trim() : '';
        const normalizedCss = css ? css.trim() : '';
        const lastSaved = lastSavedContentRef.current;
        const normalizedLastHtml = lastSaved.html ? lastSaved.html.trim() : '';
        const normalizedLastCss = lastSaved.css ? lastSaved.css.trim() : '';
        
        // Check if content has changed from last saved
        if (normalizedHtml !== normalizedLastHtml || normalizedCss !== normalizedLastCss) {
          setHasUnsavedChanges(true);
        } else {
          setHasUnsavedChanges(false);
        }
      }, 100);
    };

    editor.on('update', handleUpdate);
    editor.on('component:update', handleUpdate);
    editor.on('style:update', handleUpdate);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      editor.off('update', handleUpdate);
      editor.off('component:update', handleUpdate);
      editor.off('style:update', handleUpdate);
    };
  }, [websiteId, selectedAudience, isInitialLoad]);

  // Manual save function
  const handleSave = async () => {
    if (!editorRef.current || !selectedAudience || !websiteId || isSaving) return;

    const editor = editorRef.current;
    setIsSaving(true);

    try {
      editor.__reconcileManagedResponsiveCss?.();
      const html = editor.getHtml();
      const css = editor.getCss();
      const components = editor.getComponents().toJSON();
      
      const result = await websitesAPI.saveContent(websiteId, selectedAudience, html, css, components);
      
      // Update last saved content
      lastSavedContentRef.current = { html, css };
      setHasUnsavedChanges(false);
      
      // Reload blob URLs after saving
      if (websiteId) {
        websitesAPI.getBlobUrls(websiteId)
          .then(urls => {
            setBlobUrls(urls);
          })
          .catch(err => {
            console.error('Failed to reload blob URLs:', err);
          });
      }
    } catch (err) {
      console.error('Failed to save content:', err);
      const errorMessage = err.message || 'Failed to save changes. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Load content when website or audience changes
  useEffect(() => {
    if (!editorRef.current || !websiteId || !selectedAudience) return;

    const editor = editorRef.current;
    setIsLoadingContent(true);
    setIsInitialLoad(true);
    setHasUnsavedChanges(false);

    // Clear the editor first to remove any existing content
    editor.setComponents('');
    editor.setStyle('');

    websitesAPI.getContent(websiteId, selectedAudience)
      .then(content => {
        // Clear editor again before loading (in case API returned quickly)
        editor.setComponents('');
        editor.setStyle('');
        
        let loadedHtml = '';
        let loadedCss = '';

        if (content.components) {
          try {
            editor.setComponents(content.components);
            loadedHtml = editor.getHtml();
          } catch {
            editor.setComponents('');
          }
        } else if (content.html && content.html.trim()) {
          loadedHtml = content.html;
          editor.setComponents(loadedHtml);
        } else {
          editor.setComponents('');
        }

        if (loadedHtml || content.components) {
          setTimeout(() => {
            editor.__finalizeAllShapes?.();
            editor.__reconcileManagedResponsiveCss?.();
            const allComponents = editor.getComponents();
            allComponents.each((component) => {
              reconcileBackgroundImageOverlayRecursive(component);
            });
            ensureAllComponentSelectors(editor);
            editor.__syncAllParallax?.();
            editor.__syncAllCustomCss?.();
          }, 300);
        } else {
          editor.__reconcileManagedResponsiveCss?.();
          editor.__syncAllParallax?.();
          editor.__syncAllCustomCss?.();
        }
        
        if (content.css && content.css.trim()) {
          loadedCss = content.css;
          editor.setStyle(loadedCss);
        } else {
          // No CSS - ensure styles are cleared
          editor.setStyle('');
        }

        editor.__reconcileManagedResponsiveCss?.();
        ensureAllComponentSelectors(editor);
        editor.__syncAllParallax?.();
        editor.__syncAllCustomCss?.();

        // Re-apply uploaded fonts so the canvas keeps rendering them after content load.
        if (customFontsRef.current && customFontsRef.current.length) {
          applyCustomFonts(editor, customFontsRef.current);
        }

        // Re-inject global styles so they survive the content reload.
        injectGlobalCss(editor, globalCssRef.current);

        // Update last saved content reference
        lastSavedContentRef.current = { html: loadedHtml, css: loadedCss };
        
        setIsLoadingContent(false);
        // Allow change tracking after a short delay to let editor settle
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 500);
      })
      .catch(err => {
        console.error('Failed to load content:', err);
        // If no content exists or error occurred, ensure editor is empty
        editor.setComponents('');
        editor.setStyle('');
        lastSavedContentRef.current = { html: '', css: '' };
        setIsLoadingContent(false);
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 500);
      });
  }, [websiteId, selectedAudience, audienceContentRevision]);

  // Block in-app and browser back/forward navigation when there are unsaved changes
  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    [hasUnsavedChanges]
  );
  const navigationBlocker = useBlocker(shouldBlockNavigation);

  useEffect(() => {
    if (navigationBlocker.state !== 'blocked') return;

    const confirmed = window.confirm(UNSAVED_CHANGES_LEAVE_MESSAGE);
    if (confirmed) {
      navigationBlocker.proceed();
    } else {
      navigationBlocker.reset();
    }
  }, [navigationBlocker, navigationBlocker.state]);

  // Warn on tab close or full page reload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = UNSAVED_CHANGES_LEAVE_MESSAGE;
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Load audiences for the website
  useEffect(() => {
    if (!websiteId) return;
    
    audiencesAPI.getAll(websiteId).then(data => {
      setAudiences(data);
      if (data.length > 0 && !selectedAudience) {
        setSelectedAudience(data[0].id);
      }
    }).catch(err => {
      console.error('Failed to load audiences:', err);
    });
  }, [websiteId]);

  // Load blob URLs for all audiences
  useEffect(() => {
    if (!websiteId) return;
    
    websitesAPI.getBlobUrls(websiteId)
      .then(urls => {
        setBlobUrls(urls);
      })
      .catch(err => {
        console.error('Failed to load blob URLs:', err);
      });
  }, [websiteId, isSaving]); // Reload when saving completes

  // Add/update audience trait when audiences are loaded or change
  useEffect(() => {
    if (!editorRef.current || audiences.length === 0) return;

    const editor = editorRef.current;
    
    // Function to recursively add/update audience trait to components
    const addOrUpdateAudienceTrait = (component) => {
      if (!component) return;
      
      // Skip if it's a text node or similar
      if (component.get('type') === 'textnode') return;

      const traits = component.get('traits') || [];
      const audienceTrait = traits.find(trait => trait.name === 'data-audience');
      
      const audienceOptions = [
        { value: '', name: 'All Audiences' },
        ...audiences.map(aud => ({ value: aud.id, name: aud.name }))
      ];

      if (audienceTrait) {
        // Update existing trait
        audienceTrait.set('options', audienceOptions);
      } else {
        // Add new trait
        component.addTrait({
          type: 'select',
          name: 'data-audience',
          label: 'Audience',
          changeProp: 1,
          options: audienceOptions,
        });

        // Set default attribute
        if (!component.getAttributes()['data-audience']) {
          component.addAttributes({ 'data-audience': '' });
        }
      }

      // Recursively process children
      const components = component.get('components');
      if (components && components.length > 0) {
        components.each((child) => {
          addOrUpdateAudienceTrait(child);
        });
      }
    };

    // Process all existing components
    const components = editor.getComponents();
    components.each((component) => {
      addOrUpdateAudienceTrait(component);
    });

    // Add trait to new components
    const handleComponentAdd = (component) => {
      addOrUpdateAudienceTrait(component);
    };

    editor.on('component:add', handleComponentAdd);
    editor.on('component:create', handleComponentAdd);

    return () => {
      editor.off('component:add', handleComponentAdd);
      editor.off('component:create', handleComponentAdd);
    };
  }, [audiences]);

  // Filter components based on selected audience
  useEffect(() => {
    if (!editorRef.current || selectedAudience === null) return;

    const editor = editorRef.current;
    const canvas = editor.Canvas;
    const frame = canvas.getFrameEl();

    if (!frame) return;

    // Add CSS to hide components that don't match the selected audience
    const styleId = 'audience-filter-style';
    let styleEl = document.getElementById(styleId);
    
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    // Create CSS rule to hide components that don't match
    const css = `
      [data-audience]:not([data-audience=""]):not([data-audience="${selectedAudience}"]) {
        display: none !important;
      }
    `;

    // Apply to iframe if it exists
    if (frame.contentDocument) {
      let iframeStyle = frame.contentDocument.getElementById(styleId);
      if (!iframeStyle) {
        iframeStyle = frame.contentDocument.createElement('style');
        iframeStyle.id = styleId;
        frame.contentDocument.head.appendChild(iframeStyle);
      }
      iframeStyle.textContent = css;
    } else {
      styleEl.textContent = css;
    }

    // Also update the canvas view
    editor.refresh();
  }, [selectedAudience]);

  const handleAudienceChange = (audienceId) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to switch audiences? Your changes will be lost.');
      if (!confirmed) {
        return;
      }
    }
    setSelectedAudience(audienceId);
  };

  const handleAudiencesChange = (updatedAudiences) => {
    setAudiences(updatedAudiences);
    // If the selected audience was deleted, select the first one
    if (updatedAudiences.length > 0 && !updatedAudiences.find(a => a.id === selectedAudience)) {
      setSelectedAudience(updatedAudiences[0].id);
    }
  };

  const handleSiteStorageChange = async (e) => {
    const val = e.target.value;
    setWebsiteStorageTargetId(val);
    if (!websiteId) return;
    try {
      await websitesAPI.update(websiteId, {
        storageTargetId: val === '' ? null : val,
      });
    } catch (err) {
      alert(err.message || 'Failed to update site publish target');
      try {
        const w = await websitesAPI.getById(websiteId);
        setWebsiteStorageTargetId(w.storageTargetId ? String(w.storageTargetId) : '');
      } catch {
        /* ignore */
      }
    }
  };

  const handleSaveGlobalCss = async () => {
    if (!websiteId || isSavingGlobalCss) return;
    setIsSavingGlobalCss(true);
    try {
      await websitesAPI.update(websiteId, { globalCss: globalCssDraft });
      globalCssRef.current = globalCssDraft;
      setGlobalCss(globalCssDraft);
      if (editorRef.current) {
        injectGlobalCss(editorRef.current, globalCssDraft);
      }
    } catch (err) {
      console.error('Failed to save global styles:', err);
      alert(err.message || 'Failed to save global styles');
    } finally {
      setIsSavingGlobalCss(false);
    }
  };

  const handleAudienceContentImported = (audienceId) => {
    if (String(audienceId) !== String(selectedAudience)) return;
    setAudienceContentRevision((n) => n + 1);
    if (websiteId) {
      websitesAPI
        .getBlobUrls(websiteId)
        .then(setBlobUrls)
        .catch(() => {});
    }
  };

  const getPublishedUrl = (audienceId) => {
    if (audienceId == null) return null;
    const htmlUrl = blobUrls[audienceId]?.htmlUrl;
    if (!htmlUrl) return null;
    const baseUrl = process.env.REACT_APP_BLOB_STORAGE_BASE_URL || '';
    if (baseUrl) {
      const urlMatch = htmlUrl.match(/https?:\/\/[^/]+(\/.*)/);
      if (urlMatch) {
        return `${baseUrl.replace(/\/$/, '')}${urlMatch[1]}`;
      }
    }
    return htmlUrl;
  };

  const openFonts = () => {
    if (editorRef.current) {
      if (editorRef.current.Commands.get('open-fonts')) {
        editorRef.current.runCommand('open-fonts');
      } else {
        alert('Fonts panel is currently unavailable.');
      }
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  return (
    <div className="web-builder-container">
      <div className="web-builder-topbar">
        {/* Left Section: Navigation and Website Info */}
        <div className="topbar-left">
          <button className="back-button" onClick={handleBackClick} title="Back to Websites">
            ←
          </button>
          <div className="topbar-left-main">
            <div className="website-info-compact">
              {isEditingName ? (
                <div className="edit-field-compact">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveWebsiteInfo();
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                    className="edit-input-compact"
                    autoFocus
                    placeholder="Website name"
                  />
                  <div className="edit-actions-compact">
                    <button className="save-small-btn" onClick={handleSaveWebsiteInfo} disabled={isSavingWebsite}>
                      ✓
                    </button>
                    <button className="cancel-small-btn" onClick={handleCancelEdit}>✕</button>
                  </div>
                </div>
              ) : (
                <h2 className="website-title-compact" onClick={handleStartEditName} title="Click to edit">
                  {websiteName || 'Loading...'}
                </h2>
              )}
            </div>
          </div>
        </div>

        {/* Center Section: Tools and Audience */}
        <div className="topbar-center">
          <div className="audience-selector-compact">
            <AudienceSelector
              websiteId={websiteId}
              selectedAudience={selectedAudience}
              onAudienceChange={handleAudienceChange}
              onAudiencesChange={handleAudiencesChange}
              storageTargets={storageTargets}
              onAudienceContentImported={handleAudienceContentImported}
              showManagementControls={false}
            />
            {(() => {
              const openUrl = getPublishedUrl(selectedAudience);
              return (
                <button
                  type="button"
                  className="open-page-btn"
                  onClick={() => openUrl && window.open(openUrl, '_blank', 'noopener,noreferrer')}
                  disabled={!openUrl}
                  title={openUrl ? 'Open page in new tab' : 'Page not published yet'}
                  aria-label="Open page in new tab"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </button>
              );
            })()}
          </div>
        </div>

        {/* Right Section: Save and Status */}
        <div className="topbar-right">
          {isLoadingContent && (
            <span className="status-indicator loading">Loading...</span>
          )}
          {hasUnsavedChanges && !isLoadingContent && (
            <span className="status-indicator unsaved">Unsaved changes</span>
          )}
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => setShowSettingsModal(true)}
            title="Open page settings"
          >
            <span className="toolbar-icon">⚙️</span>
            <span className="toolbar-label">Settings</span>
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => setShowVersionHistory(!showVersionHistory)}
            title="View version history"
          >
            <span className="toolbar-icon">🕒</span>
            <span className="toolbar-label">Versions</span>
          </button>
          <button 
            className={`save-button ${hasUnsavedChanges ? 'has-changes' : ''}`}
            onClick={handleSave}
            disabled={isSaving || !hasUnsavedChanges}
            title={hasUnsavedChanges ? 'Save changes' : 'No changes to save'}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      <div id="gjs"></div>

      <BackgroundImagePickerModal
        open={bgImagePickerOpen}
        websiteId={websiteId}
        currentUrl={bgImagePickerCurrentUrl}
        title={bgImagePickerTitle}
        onClose={() => {
          setBgImagePickerOpen(false);
          bgImagePickerOnSelectRef.current = null;
        }}
        onSelect={(url) => {
          const cb = bgImagePickerOnSelectRef.current;
          if (typeof cb === 'function') cb(url);
          setBgImagePickerOpen(false);
          bgImagePickerOnSelectRef.current = null;
        }}
      />

      <FontManagerModal
        open={fontManagerOpen}
        websiteId={websiteId}
        hasGoogleFonts={!!editorRef.current?.Commands?.get('open-fonts')}
        onClose={() => setFontManagerOpen(false)}
        onFontsChanged={loadCustomFonts}
        onOpenGoogleFonts={() => {
          setFontManagerOpen(false);
          openFonts();
        }}
      />

      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Page Settings</h3>
            <div className="settings-quick-actions">
              <div className="settings-publishing-action">
                <button type="button" className="toolbar-btn" title="Default publish domain / storage for this site">
                  <span className="toolbar-icon">🌐</span>
                  <span className="toolbar-label">Publishing</span>
                </button>
                {storageTargets.length > 0 && websiteId ? (
                  <select
                    className="storage-target-inline-select"
                    value={websiteStorageTargetId}
                    onChange={handleSiteStorageChange}
                    title="Default publish domain / storage for this site"
                  >
                    <option value="">Auto (first configured)</option>
                    {storageTargets.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className="settings-empty">No targets</span>
                )}
              </div>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => {
                  setShowSettingsModal(false);
                  setFontManagerOpen(true);
                }}
              >
                <span className="toolbar-icon">🔤</span>
                <span className="toolbar-label">Fonts</span>
              </button>
              <button
                type="button"
                className="toolbar-btn"
                onClick={() => {
                  setShowSettingsModal(false);
                  navigate(`/editor/${websiteId}/statistics`);
                }}
              >
                <span className="toolbar-icon">📊</span>
                <span className="toolbar-label">Statistics</span>
              </button>
            </div>

            <h4 className="settings-block-title">Global Styles (CSS)</h4>
            <div className="global-css-block">
              <p className="global-css-hint">
                CSS added here applies to every audience of this page (both in the
                editor preview and on the published page). Global rules are applied
                after each audience's own styles.
              </p>
              <textarea
                className="global-css-textarea"
                value={globalCssDraft}
                onChange={(e) => setGlobalCssDraft(e.target.value)}
                spellCheck={false}
                rows={10}
                placeholder={'/* e.g. */\nbody { font-family: Inter, sans-serif; }\na { color: #2563eb; }'}
              />
              <div className="global-css-actions">
                {globalCssDraft !== globalCss && (
                  <span className="global-css-dirty">Unsaved changes</span>
                )}
                <button
                  type="button"
                  className="global-css-reset"
                  onClick={() => setGlobalCssDraft(globalCss)}
                  disabled={isSavingGlobalCss || globalCssDraft === globalCss}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="global-css-save"
                  onClick={handleSaveGlobalCss}
                  disabled={isSavingGlobalCss || globalCssDraft === globalCss}
                >
                  {isSavingGlobalCss ? 'Saving…' : 'Save Global Styles'}
                </button>
              </div>
            </div>

            <h4 className="settings-block-title">Audience Management</h4>
            <AudienceSelector
              websiteId={websiteId}
              selectedAudience={selectedAudience}
              onAudienceChange={handleAudienceChange}
              onAudiencesChange={handleAudiencesChange}
              storageTargets={storageTargets}
              onAudienceContentImported={handleAudienceContentImported}
              showManagementControls
              showLabel={false}
              managementLayout="list"
            />

            <h4 className="settings-block-title">Published Links</h4>
            <div className="blob-links-dropdown settings-links-list">
              <div className="blob-links-header">Published Pages</div>
              {audiences.length === 0 ? (
                <div className="blob-links-empty">No audiences yet</div>
              ) : (
                audiences.map((audience) => {
                  const blobUrl = blobUrls[audience.id];
                  const htmlUrl = blobUrl?.htmlUrl;
                  const baseUrl = process.env.REACT_APP_BLOB_STORAGE_BASE_URL || '';
                  let displayUrl = htmlUrl;
                  if (htmlUrl && baseUrl) {
                    const urlMatch = htmlUrl.match(/https?:\/\/[^\/]+(\/.*)/);
                    if (urlMatch) {
                      const path = urlMatch[1];
                      const cleanBaseUrl = baseUrl.replace(/\/$/, '');
                      displayUrl = `${cleanBaseUrl}${path}`;
                    }
                  }
                  return (
                    <div key={audience.id} className="blob-link-item">
                      <div className="blob-link-label">{audience.name}</div>
                      {displayUrl ? (
                        <a
                          href={displayUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="blob-link"
                        >
                          Open in new tab →
                        </a>
                      ) : (
                        <span className="blob-link-unavailable">Not published yet</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowSettingsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Version History Sidebar */}
      {showVersionHistory && (
        <>
          <div className="version-history-overlay" onClick={() => setShowVersionHistory(false)}></div>
          <VersionHistory
            websiteId={websiteId}
            audienceId={selectedAudience}
            isOpen={showVersionHistory}
            onClose={() => setShowVersionHistory(false)}
            onRestore={async () => {
              // Reload content after restore
              if (selectedAudience && editorRef.current) {
                setIsLoadingContent(true);
                try {
                  // Wait a moment for the server to update
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  const content = await websitesAPI.getContent(websiteId, selectedAudience);
                  const editor = editorRef.current;
                  
                  // Clear editor
                  editor.setComponents('');
                  editor.setStyle('');
                  
                  // Set new content
                  if (content.components) {
                    editor.setComponents(content.components);
                  } else if (content.html) {
                    editor.setComponents(content.html);
                  }
                  if (content.css) {
                    editor.setStyle(content.css);
                  }
                  editor.__reconcileManagedResponsiveCss?.();
                  editor.__syncAllCustomCss?.();

                  // Update last saved content
                  const html = editor.getHtml();
                  const css = editor.getCss();
                  lastSavedContentRef.current = { html, css };
                  setHasUnsavedChanges(false);
                  
                  // Reload blob URLs
                  const urls = await websitesAPI.getBlobUrls(websiteId);
                  setBlobUrls(urls);
                  
                  // Close the version history sidebar
                  setShowVersionHistory(false);
                } catch (err) {
                  console.error('Failed to reload content:', err);
                  alert('Failed to reload content. Please refresh the page.');
                } finally {
                  setIsLoadingContent(false);
                }
              }
            }}
          />
        </>
      )}
    </div>
  );
}

export default WebBuilder;